import React, { useState, useEffect, useRef, useCallback } from 'react';
import { SearchLoader, useSearchLoaderPhase } from './components/SearchLoader';
import { promptAfterReload } from './components/TrialUpgrade';
import { tourEvent, useLessonAction } from './tour/tourEvents';
import { useTour } from './tour/TourProvider';
import { GoogleMap } from '@react-google-maps/api';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { useAuth } from './AuthContext';
import { SearchTemplatesBar } from './SearchTemplates';
import { releaseLeaveGuard } from './navGuard';
import { LIVE_SEARCH_INDUSTRIES } from './liveSearchIndustries';
import { LIVE_SEARCH_COMPANY_TYPES, LIVE_SEARCH_FUNDING_TYPES, LIVE_SEARCH_JOB_FUNCTIONS } from './liveSearchEnums';

/**
 * V2 Lab — sandboxed explorer for purchased candidate datasets.
 * Everything lives in its own v2_* tables; nothing here reads or writes
 * candidates, companies or projects. The layout mirrors the project tab:
 * map + table + per-person detail panel, so a purchased dataset can be
 * judged the same way a real project is.
 */

interface Dataset {
  id: number;
  name: string;
  filename: string | null;
  status: string;
  status_detail: string | null;
  record_count: number;
  error_count: number;
  created_at: string;
  kind?: string;                       // 'people' | 'companies'
  linked_dataset_id?: number | null;   // people → companies link
  geocoded_count?: number;
  mapping?: Record<string, string | string[]> | null;
  field_stats?: Record<string, { count: number; types: Record<string, number>; samples: string[] }> | null;
}

interface CompanyRow {
  id: number;
  name: string | null;
  industry: string | null;
  headcount: number | null;
  headcount_text: string | null;
  domain: string | null;
  linkedin_url: string | null;
  location: string | null;
}

interface PersonRow {
  id: number;
  name: string | null;
  title: string | null;
  company: string | null;
  location: string | null;
  linkedin_url: string | null;
  headline: string | null;
  email: string | null;
  phone: string | null;
  geocoded: boolean;
  skills?: string | null;
  current_start?: string | null;
  experience_years?: number | string | null;
  employment_type?: string | null;
}

const yearsSince = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '';
  const t = new Date(dateStr).getTime();
  if (isNaN(t)) return '';
  return Math.max(0, (Date.now() - t) / 31557600000).toFixed(1);
};

interface Stats {
  kind?: string;
  coverage: Record<string, string | number>;
  topCompanies?: { value: string; n: string }[];
  topLocations?: { value: string; n: string }[];
  topTitles?: { value: string; n: string }[];
  topIndustries?: { value: string; n: string }[];
}

const COMPANY_CONCEPTS: { key: string; label: string; note: string }[] = [
  { key: 'name', label: 'Company name', note: 'Row identity.' },
  { key: 'linkedin_url', label: 'LinkedIn URL', note: 'THE join key — matches people’s role history to these companies.' },
  { key: 'domain', label: 'Domain', note: 'Fallback join key + email finding.' },
  { key: 'website', label: 'Website', note: 'Company pins and enrichment.' },
  { key: 'industry', label: 'Industry', note: 'Industry filter in people search.' },
  { key: 'headcount', label: 'Headcount', note: 'Employee-count filter in people search.' },
  { key: 'specialties', label: 'Specialties', note: 'Specialties filter in people search.' },
  { key: 'description', label: 'Description', note: 'Description keyword filter in people search.' },
  { key: 'location', label: 'Location', note: 'Company geography.' },
];

const CONCEPTS: { key: string; label: string; note: string }[] = [
  { key: 'name', label: 'Name', note: 'Row identity across the whole app.' },
  { key: 'linkedin_url', label: 'LinkedIn URL', note: 'Dedup key — enrichment, caching and email finding all hang off it.' },
  { key: 'title', label: 'Job title', note: 'Filtering, keyword scoring, job spec matching.' },
  { key: 'company', label: 'Company', note: 'Company pins, associations, connections.' },
  { key: 'location', label: 'Location', note: 'Geocoding — without it there is no map.' },
  { key: 'headline', label: 'Headline', note: 'Context for Ada and AI columns.' },
  { key: 'email', label: 'Email', note: 'Outreach without paying a contact-finding provider.' },
  { key: 'phone', label: 'Phone', note: 'Direct outreach.' },
  { key: 'skills', label: 'Skills', note: 'Evidence for keyword criteria scoring.' },
];

const PAGE_SIZE = 50;

// Vendor employment labels grouped into recruiter-shaped options (most
// popular first). The UI shows the group; the search expands it to every
// underlying vendor label.
export const EMPLOYMENT_TYPE_GROUPS: { label: string; values: string[] }[] = [
  { label: 'Permanent', values: ['Permanent', 'Full Time', 'Permanent / Full-Time'] },
  { label: 'Contract / Full-Time', values: ['Contract / Full-Time', 'Direct Contract'] },
  { label: 'Freelance', values: ['Contract', 'Freelance'] },
  { label: 'Self Employed', values: ['Self Employed'] },
  { label: 'Indirect Contract', values: ['Indirect Contract'] },
  { label: 'Part Time', values: ['Part Time', 'Permanent / Part-Time', 'Contract / Part-Time'] },
  { label: 'Internship', values: ['Internship', 'Civil Service Internship', 'Co-op', 'Work-Study'] },
  { label: 'Apprenticeship', values: ['Apprenticeship', 'Leadership Program'] },
  { label: 'Seasonal', values: ['Seasonal', 'On Call', 'Recurring Status'] },
  { label: 'Public Sector', values: ['Lifetime Civil Servant', 'Temporary Civil Servant'] },
  { label: 'Volunteer Programs', values: ['International Volunteer Program'] },
];
export const expandEmploymentGroups = (labels: string[]) =>
  labels.flatMap((l) => EMPLOYMENT_TYPE_GROUPS.find((g) => g.label === l)?.values || [l]);
// The reverse, for a saved search: a group whose every value is there
// comes back as its chip.
export const collapseEmploymentGroups = (values: string[]) => {
  let rest = [...values];
  const out: string[] = [];
  for (const g of EMPLOYMENT_TYPE_GROUPS) {
    if (g.values.length && g.values.every((v) => rest.includes(v))) {
      out.push(g.label);
      rest = rest.filter((v) => !g.values.includes(v));
    }
  }
  return [...out, ...rest];
};

// A Keywords group: ANY of its words, in ANY of its ticked fields; groups
// AND together (server: keywordGroups in services/foragerSearch.js).
export interface KeywordGroup { terms: string[]; exclude: string[]; fields: string[] }
export const KEYWORD_FIELDS: { key: string; label: string }[] = [
  { key: 'headline', label: 'Headline' }, { key: 'summary', label: 'Summary' }, { key: 'skills', label: 'Skills' },
  { key: 'role_current', label: 'Current roles' }, { key: 'role_past', label: 'Past roles' },
];

export interface SearchFilters {
  keywordGroups: KeywordGroup[];
  // Legacy per-field keywords — still accepted by the server (API, older
  // saved searches); the UI turns them into keyword groups (withKeywordGroups).
  titles: string[]; titleScope: string; locations: string[]; headlineKeywords: string[]; summaryKeywords: string[];
  anywhereKeywords: string[];
  skills: string[];
  roleKeywords: string[]; roleScope: string;
  employmentTypes: string[]; employmentScope: string;
  industries: string[]; industryScope: string;
  companies: string[]; companyScope: string;
  schools: string[]; degrees: string[];
  tenureMin: string; tenureMax: string; joinedMin: string; joinedMax: string; experienceMin: string; experienceMax: string;
  // Spoken languages (+ optional minimum level) and certifications — from the
  // universe's people_extras side table.
  languages: string[]; languageLevel: string; certifications: string[];
  // Within nearMiles of a postcode / town (free GeoNames point; distance to
  // each person's town).
  nearPlace: string; nearMiles: string;
  // NOT terms (⊘ on a chip) and employment type from profile wording.
  titlesExclude: string[]; skillsExclude: string[]; headlineKeywordsExclude: string[];
  employmentSignals: boolean;
}

export const EMPTY_FILTERS: SearchFilters = {
  keywordGroups: [],
  titles: [], titleScope: 'current', locations: [], headlineKeywords: [], summaryKeywords: [],
  anywhereKeywords: [],
  skills: [],
  roleKeywords: [], roleScope: 'current',
  employmentTypes: [], employmentScope: 'current',
  industries: [], industryScope: 'current',
  companies: [], companyScope: 'current',
  schools: [], degrees: [],
  tenureMin: '', tenureMax: '', joinedMin: '', joinedMax: '', experienceMin: '', experienceMax: '',
  languages: [], languageLevel: '', certifications: [],
  nearPlace: '', nearMiles: '25',
  titlesExclude: [], skillsExclude: [], headlineKeywordsExclude: [], employmentSignals: false,
};

// Companies search mode — filters over a companies dataset.
interface CompanyFilters {
  nameKeywords: string[]; industries: string[]; locations: string[];
  headcountMin: string; headcountMax: string;
  foundedAfter: string; foundedBefore: string;
  descriptionKeywords: string[]; keywords: string[]; technologies: string[];
  companiesList: string[];
}
const EMPTY_COMPANY_FILTERS: CompanyFilters = {
  nameKeywords: [], industries: [], locations: [],
  headcountMin: '', headcountMax: '',
  foundedAfter: '', foundedBefore: '',
  descriptionKeywords: [], keywords: [], technologies: [],
  companiesList: [],
};

// Per-field keyword lists (older searches, AI output) → keyword groups, so
// one Keywords section shows everything. Same matches, except role-description
// words no longer have to be on the same role as the title.
export const withKeywordGroups = (f: SearchFilters): SearchFilters => {
  const groups: KeywordGroup[] = [...(f.keywordGroups || [])];
  const add = (terms: string[], fields: string[], exclude: string[] = []) => {
    if ((terms && terms.length) || exclude.length) groups.push({ terms: terms || [], exclude, fields });
  };
  add(f.anywhereKeywords, ['headline', 'summary', 'skills', 'role_current', 'role_past']);
  add(f.headlineKeywords, ['headline'], f.headlineKeywordsExclude || []);
  add(f.summaryKeywords, ['summary']);
  add(f.roleKeywords, f.roleScope === 'past' ? ['role_past'] : f.roleScope === 'either' ? ['role_current', 'role_past'] : ['role_current']);
  return {
    ...f, keywordGroups: groups,
    anywhereKeywords: [], headlineKeywords: [], headlineKeywordsExclude: [], summaryKeywords: [], roleKeywords: [],
  };
};
export const keywordGroupsPayload = (f: SearchFilters) =>
  (f.keywordGroups || []).filter((g) => g.fields.length && (g.terms.length || g.exclude.length));

// How many filters a people filter set carries (persona tabs + badges).
// NOT terms + the employment wording switch, as the search API takes them.
export const notPayload = (f: SearchFilters) => ({
  titlesExclude: f.titlesExclude || [], skillsExclude: f.skillsExclude || [],
  headlineKeywordsExclude: f.headlineKeywordsExclude || [],
  employmentSignals: f.employmentSignals && f.employmentTypes.length > 0 ? true : undefined,
});
// Employment groups the profile-wording option applies to.
export const signalsApply = (types: string[]) => types.some((t) => /freelanc|self|contract|temp|interim/i.test(t));
export const nearPayload = (f: SearchFilters) =>
  (f.nearPlace && f.nearPlace.trim() ? { place: f.nearPlace.trim(), miles: Number(f.nearMiles) || 25 } : undefined);
export const countPeopleFilters = (f: SearchFilters) =>
  (f.nearPlace && f.nearPlace.trim() ? 1 : 0) +
  (f.titlesExclude || []).length + (f.skillsExclude || []).length + (f.headlineKeywordsExclude || []).length +
  f.titles.length + f.locations.length + f.anywhereKeywords.length + f.headlineKeywords.length +
  f.summaryKeywords.length + f.skills.length + f.roleKeywords.length + f.employmentTypes.length +
  f.industries.length + f.companies.length + f.schools.length + f.degrees.length +
  (f.tenureMin ? 1 : 0) + (f.tenureMax ? 1 : 0) +
  (f.joinedMin ? 1 : 0) + (f.joinedMax ? 1 : 0) +
  (f.experienceMin ? 1 : 0) + (f.experienceMax ? 1 : 0) +
  (f.languages || []).length + (f.certifications || []).length +
  (f.keywordGroups || []).reduce((n, g) => n + g.terms.length + g.exclude.length, 0);

// How many filters a company filter set carries.
const countCompanyFilters = (c: CompanyFilters) =>
  c.nameKeywords.length + c.industries.length + c.locations.length +
  c.descriptionKeywords.length + c.keywords.length + c.technologies.length + c.companiesList.length +
  (c.headcountMin ? 1 : 0) + (c.headcountMax ? 1 : 0) + (c.foundedAfter ? 1 : 0) + (c.foundedBefore ? 1 : 0);

// Badge palette per persona (index-stable), shared with Contact Search.
export const PERSONA_COLORS = [
  { bg: 'bg-teal-100', text: 'text-teal-700', solid: 'bg-teal-500' },
  { bg: 'bg-violet-100', text: 'text-violet-700', solid: 'bg-violet-500' },
  { bg: 'bg-amber-100', text: 'text-amber-700', solid: 'bg-amber-500' },
  { bg: 'bg-rose-100', text: 'text-rose-700', solid: 'bg-rose-500' },
  { bg: 'bg-sky-100', text: 'text-sky-700', solid: 'bg-sky-500' },
  { bg: 'bg-lime-100', text: 'text-lime-700', solid: 'bg-lime-600' },
];

// Pasted text / CSV → company entries (LinkedIn company URLs, domains, or
// names). URLs and domains are pulled out of any CSV cell; a line with no
// separators and no URL/domain is treated as a company name.
// Domains that belong to a platform, never to the company being named.
const NOT_COMPANY_DOMAINS = /^(?:[a-z]{2,3}\.)?(linkedin\.com|lnkd\.in)$/i;

export function parseCompanyEntries(text: string): string[] {
  const out: string[] = [];
  for (const line of String(text || '').split(/\r?\n/)) {
    const cells = line.split(/[\t,;]/).map(c => c.trim().replace(/^["']|["']$/g, '')).filter(Boolean);
    if (!cells.length) continue;
    // Company and showcase pages are companies (matched by their slug).
    const urlCell = cells.find(c => /linkedin\.com\/(company|showcase)\//i.test(c));
    if (urlCell) { out.push(urlCell); continue; }
    // Any other LinkedIn link (a person, a school, a post) is NOT a company —
    // and must never fall through to "domain linkedin.com", which matched
    // every company whose website is recorded as its LinkedIn page (people
    // at LinkedIn, Time Warner Cable… in a pharma list, 22 Oct 2026).
    const domCell = cells.find(c => {
      const host = c.replace(/^https?:\/\//i, '').replace(/^www\./i, '').split('/')[0];
      return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/i.test(host) && !NOT_COMPANY_DOMAINS.test(host);
    });
    if (domCell) {
      out.push(domCell.replace(/^https?:\/\//i, '').replace(/^www\./i, '').split('/')[0]);
      continue;
    }
    if (cells.length === 1 && !/linkedin\.com|lnkd\.in/i.test(cells[0])) out.push(cells[0]);
  }
  return Array.from(new Set(out.map(s => s.trim()).filter(Boolean))).slice(0, 25000);
}

// Multi-value chip input: type, press Enter to add, click × to remove.
// `suggestions` renders a datalist for enum-backed fields (exact values).
// With excluded / onExcludedChange, each chip has a ⊘ toggle that turns it
// into a NOT term (red, struck through) and back — LinkedIn's "not".
// The describe-search box is one line, so a pasted list would lose its line
// breaks ("Head of X Formulation Scientist …"). Pasted lines become
// "A; B; C" — after a colon when there's an instruction before them — which
// the search builder reads as a list to use exactly as given.
export const pasteListIntoPrompt = (e: React.ClipboardEvent<HTMLInputElement>, current: string, set: (v: string) => void) => {
  const text = e.clipboardData.getData('text');
  if (!/\r?\n/.test(text.trim())) return;
  e.preventDefault();
  const items = text.split(/\r?\n/).map((x) => x.replace(/^\s*(?:[-*•·]|\d+[.)])\s*/, '').trim()).filter(Boolean);
  // A job spec / long text (long lines) keeps its sentences: lines joined
  // with full stops, not turned into a list.
  const longText = items.filter((x) => x.length > 80).length > items.length * 0.2;
  const el = e.currentTarget;
  const start = el.selectionStart ?? current.length;
  const end = el.selectionEnd ?? current.length;
  const before = current.slice(0, start);
  if (longText) {
    const prose = items.map((x) => (/[.!?:;]$/.test(x) ? x : `${x}.`)).join(' ');
    set(`${before}${before.trim() && !/\s$/.test(before) ? ' ' : ''}${prose}${current.slice(end)}`);
    return;
  }
  const joined = items.join('; ');
  set(`${before}${before.trim() && !/[:;,]\s*$/.test(before) ? ': ' : ''}${joined}${current.slice(end)}`);
};

// What a described search asked for that filters can't check (years IN a
// skill, sector experience, team size…) — the builder lists them, and Claude
// with the MarketMapr connector can check them by reading each career.
export const BuildGaps: React.FC<{ items: string[] }> = ({ items }) => (items.length ? (
  <div className="mt-1.5 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-3 py-2">
    <div className="font-medium">Not in these filters — a search can't check:</div>
    <ul className="mt-0.5 list-disc pl-4">
      {items.map((x) => <li key={x}>{x}</li>)}
    </ul>
    <div className="mt-1 text-amber-800">
      Claude can: with MarketMapr connected it reads each person's career history and checks these for you.{' '}
      <a href="/settings?tab=integrations" className="underline font-medium">Use MarketMapr in Claude →</a>
    </div>
  </div>
) : null);

export const ChipInput: React.FC<{ label: string; values: string[]; onChange: (v: string[]) => void; placeholder?: string; suggestions?: string[]; suggestionLabels?: Record<string, string>; suggestionsId?: string; splitCommas?: boolean; excluded?: string[]; onExcludedChange?: (excluded: string[], values: string[]) => void }> =
  ({ label, values, onChange, placeholder, suggestions, suggestionLabels, suggestionsId, splitCommas, excluded, onExcludedChange }) => {
    const notList = excluded || [];
    const canExclude = !!onExcludedChange;
    const [draft, setDraft] = useState('');
    // Commas make separate chips ("python, java" = two searches) unless
    // turned off (locations: "Birmingham, United Kingdom" is one place) or
    // the text is exactly a suggestion ("Health, Wellness and Fitness").
    const add = () => {
      const whole = !!suggestions && suggestions.indexOf(draft.trim()) !== -1;
      const parts = (splitCommas !== false && !whole ? draft.split(',') : [draft])
        .map((s) => s.trim()).filter((s) => s && values.indexOf(s) === -1);
      if (parts.length) onChange(values.concat(Array.from(new Set(parts))));
      setDraft('');
    };
    const listId = suggestions && suggestions.length ? (suggestionsId || `chips-${label.replace(/\W+/g, '-')}`) : undefined;
    return (
      <div>
        <div className="text-xs font-semibold text-gray-600 mb-1">{label}</div>
        {listId && (
          <datalist id={listId}>
            {suggestions!.map((s) => <option key={s} value={s} label={suggestionLabels?.[s]} />)}
          </datalist>
        )}
        <input
          className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          value={draft}
          list={listId}
          placeholder={placeholder || 'Type and press Enter'}
          onChange={(e) => {
            const v = e.target.value;
            // Picking a datalist option fires change with the exact value —
            // add the chip immediately, no extra Enter needed.
            if (suggestions && suggestions.indexOf(v) !== -1) {
              if (values.indexOf(v) === -1) onChange(values.concat([v]));
              setDraft('');
            } else {
              setDraft(v);
            }
          }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          onBlur={() => { if (draft.trim()) add(); }}
        />
        {(values.length > 0 || notList.length > 0) && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {values.map((v) => (
              <span key={v} className="inline-flex items-center gap-1 bg-teal-50 text-teal-800 text-xs px-2 py-0.5 rounded-full">
                {v}
                {canExclude && (
                  <button className="text-teal-400 hover:text-red-600" title="Exclude — NOT this"
                    onClick={() => onExcludedChange!(notList.concat([v]), values.filter((x) => x !== v))}>⊘</button>
                )}
                <button className="text-teal-500 hover:text-teal-900" onClick={() => onChange(values.filter((x) => x !== v))}>×</button>
              </span>
            ))}
            {notList.map((v) => (
              <span key={`not-${v}`} className="inline-flex items-center gap-1 bg-red-50 text-red-700 text-xs px-2 py-0.5 rounded-full" title="Excluded (NOT)">
                <span className="text-[10px] font-semibold">NOT</span>
                <span className="line-through">{v}</span>
                <button className="text-red-400 hover:text-teal-700" title="Include instead"
                  onClick={() => onExcludedChange!(notList.filter((x) => x !== v), values.concat([v]))}>⊘</button>
                <button className="text-red-400 hover:text-red-800" onClick={() => onExcludedChange!(notList.filter((x) => x !== v), values)}>×</button>
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

// Employment type also from what people write: "freelance(r)" in the
// headline / a current title / that role's description, "contract(or)" in
// the headline / a current title.
export const EmploymentSignalsToggle: React.FC<{ checked: boolean; onChange: (v: boolean) => void }> = ({ checked, onChange }) => (
  <div className="mt-2 rounded-lg border border-gray-200 p-2">
    <label className="flex items-start gap-1.5 text-xs text-gray-700">
      <input type="checkbox" className="mt-0.5" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>Also match people with no employment type set who say it in their profile:
        “freelance/freelancer” in their headline, current job title or role description; “contract/contractor” in their headline or current job title.</span>
    </label>
    {checked && (
      <div className="text-[11px] text-amber-700 mt-1">
        “Contract” is sometimes used another way (e.g. a Contract Manager). Worth a check in the project — people matched this way are the ones with no employment type set.
      </div>
    )}
  </div>
);

// "Within N miles of <postcode / town>" — the town box offers the same real
// places as the Location suggestions; a postcode is typed as-is.
export const NearInput: React.FC<{ place: string; miles: string; onChange: (place: string, miles: string) => void; authHeaders: () => Record<string, string>; tone?: 'teal' | 'violet' }> =
  ({ place, miles, onChange, authHeaders, tone = 'teal' }) => {
    const [places, setPlaces] = useState<{ label: string; people: number }[]>([]);
    const [open, setOpen] = useState(false);
    const seq = useRef(0);
    useEffect(() => {
      const q = place.trim();
      if (q.length < 2 || /\d/.test(q)) { setPlaces([]); return; } // postcodes: no suggestions
      const mine = ++seq.current;
      const t = setTimeout(() => {
        fetch(`/api/universe/places?q=${encodeURIComponent(q)}`, { headers: authHeaders() })
          .then((r) => (r.ok ? r.json() : null))
          .then((d) => { if (mine === seq.current) setPlaces((d && d.places) || []); })
          .catch(() => {});
      }, 150);
      return () => clearTimeout(t);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [place]);
    const ring = tone === 'violet' ? 'focus:ring-violet-500' : 'focus:ring-teal-500';
    return (
      <div>
        <div className="text-xs font-semibold text-gray-600 mb-1">Within</div>
        <div className="flex items-center gap-1.5">
          <input type="number" min={1} max={250} step="any" inputMode="decimal"
            className="w-16 border border-gray-300 rounded-lg px-2 py-1.5 text-sm" value={miles}
            onChange={(e) => onChange(place, e.target.value)} aria-label="Miles" />
          <span className="text-xs text-gray-500">mi</span>
          <span className="text-xs text-gray-500">of</span>
          <input
            className={`flex-1 min-w-0 border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 ${ring}`}
            value={place}
            placeholder="Postcode or town"
            onChange={(e) => { onChange(e.target.value, miles); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
          />
          {place && <button className="text-gray-400 hover:text-gray-700 text-sm" onClick={() => onChange('', miles)} aria-label="Clear">×</button>}
        </div>
        {open && places.length > 0 && (
          <div className="mt-1 bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
            {places.filter((p: any) => !p.countries).slice(0, 6).map((p) => (
              <button key={p.label} type="button" className="w-full text-left px-2.5 py-1.5 text-xs hover:bg-gray-50 text-gray-800"
                onMouseDown={(e) => { e.preventDefault(); onChange(p.label, miles); setOpen(false); }}>
                {p.label}
              </button>
            ))}
          </div>
        )}
        <div className="text-[10px] text-gray-400 mt-1">Measured to each person's town.</div>
      </div>
    );
  };

// School / Degree / Company chips with as-you-type suggestions from the data
// (/api/universe/suggest). Picking a company adds "Name (linkedin.com/company/
// slug)" — that exact company — shown as just its name; Enter adds what's
// typed as before (a name matches every company containing it).
export const companyChipLabel = (v: string) => v.replace(/\s*\((?:https?:\/\/)?(?:www\.)?linkedin\.com\/(?:company|showcase)\/[^)]*\)\s*$/i, '');
export const SuggestChipInput: React.FC<{ kind: 'school' | 'degree' | 'company'; label?: string; values: string[]; onChange: (v: string[]) => void; authHeaders: () => Record<string, string>; placeholder?: string; tone?: 'teal' | 'violet' }> =
  ({ kind, label, values, onChange, authHeaders, placeholder, tone = 'teal' }) => {
    const [draft, setDraft] = useState('');
    const [items, setItems] = useState<{ label: string; value: string; detail?: string }[]>([]);
    const [active, setActive] = useState(-1);
    const [open, setOpen] = useState(false);
    const seq = useRef(0);
    const listRef = useRef<HTMLDivElement>(null);
    useEffect(() => { if (open && items.length) listRef.current?.scrollIntoView({ block: 'nearest' }); }, [open, items]);
    useEffect(() => {
      const q = draft.trim();
      // A pasted URL / domain is added as typed: no suggestions to offer.
      if (q.length < 2 || /[/]|\.[a-z]{2,}$/i.test(q)) { setItems([]); return; }
      const mine = ++seq.current;
      const t = setTimeout(() => {
        fetch(`/api/universe/suggest?kind=${kind}&q=${encodeURIComponent(q)}`, { headers: authHeaders() })
          .then((r) => (r.ok ? r.json() : null))
          .then((d) => { if (mine === seq.current) { setItems((d && d.items) || []); setActive(-1); } })
          .catch(() => { /* free text still works */ });
      }, 150);
      return () => clearTimeout(t);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draft]);
    const add = (v: string) => {
      const x = v.trim();
      if (x && values.indexOf(x) === -1) onChange(values.concat([x]));
      setDraft(''); setItems([]); setActive(-1);
    };
    const ring = tone === 'violet' ? 'focus:ring-violet-500' : 'focus:ring-teal-500';
    const chip = tone === 'violet' ? 'bg-violet-50 text-violet-800' : 'bg-teal-50 text-teal-800';
    const shown = (v: string) => (kind === 'company' ? companyChipLabel(v) : v);
    return (
      <div>
        {label && <div className="text-xs font-semibold text-gray-600 mb-1">{label}</div>}
        <input
          className={`w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 ${ring}`}
          value={draft}
          placeholder={placeholder || 'Start typing…'}
          onChange={(e) => { setDraft(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown' && items.length) { e.preventDefault(); setActive((a) => Math.min(a + 1, items.length - 1)); }
            else if (e.key === 'ArrowUp' && items.length) { e.preventDefault(); setActive((a) => Math.max(a - 1, -1)); }
            else if (e.key === 'Enter') { e.preventDefault(); add(active >= 0 && items[active] ? items[active].value : draft); }
            else if (e.key === 'Escape') setOpen(false);
          }}
          onBlur={() => { setOpen(false); if (draft.trim()) add(draft); }}
        />
        {open && items.length > 0 && (
          <div ref={listRef} className="mt-1 bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden" role="listbox">
            {items.map((it, i) => (
              <button
                key={`${it.value}-${i}`}
                type="button"
                role="option"
                aria-selected={i === active}
                onMouseDown={(e) => { e.preventDefault(); add(it.value); }}
                onMouseEnter={() => setActive(i)}
                className={`w-full flex flex-col px-2.5 py-1.5 text-left text-xs ${i === active ? 'bg-gray-100' : 'bg-white'}`}
              >
                <span className="text-gray-800 truncate w-full">{it.label}</span>
                {it.detail && <span className="text-[10px] text-gray-400 truncate w-full">{it.detail}</span>}
              </button>
            ))}
            <div className="px-2.5 py-1 text-[10px] text-gray-400 border-t border-gray-100">
              {kind === 'company'
                ? <>Pick one for that exact company · Enter adds “{draft.trim()}” as typed (every company with it in the name)</>
                : <>Enter to add “{draft.trim()}” as typed</>}
            </div>
          </div>
        )}
        {values.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {values.map((v) => (
              <span key={v} title={v} className={`inline-flex items-center gap-1 ${chip} text-xs px-2 py-0.5 rounded-full max-w-full`}>
                <span className="truncate">{shown(v)}</span>
                <button className="opacity-60 hover:opacity-100" onClick={() => onChange(values.filter((x) => x !== v))}>×</button>
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

// Company location: a pick-list of regions (Europe, Nordics, DACH…) and every
// country companies are in, shown as soon as the box is clicked and narrowed
// as you type; Enter adds what's typed (a city works too). Regions expand to
// their countries on the server (data/regions.js).
let companyCountriesPromise: Promise<{ regions: { label: string; countries: number }[]; countries: { label: string; companies: number }[] }> | null = null;
const loadCompanyCountries = (authHeaders: () => Record<string, string>) => {
  if (!companyCountriesPromise) {
    companyCountriesPromise = fetch('/api/universe/company-countries', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : { regions: [], countries: [] }))
      .catch(() => { companyCountriesPromise = null; return { regions: [], countries: [] }; });
  }
  return companyCountriesPromise;
};
export const CompanyLocationInput: React.FC<{ values: string[]; onChange: (v: string[]) => void; authHeaders: () => Record<string, string> }> =
  ({ values, onChange, authHeaders }) => {
    const [draft, setDraft] = useState('');
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(-1);
    const [opts, setOpts] = useState<{ label: string; detail: string; region?: boolean }[]>([]);
    useEffect(() => {
      loadCompanyCountries(authHeaders).then((d) => setOpts([
        ...d.regions.map((r) => ({ label: r.label, detail: `${r.countries} countries`, region: true })),
        ...d.countries.map((c) => ({ label: c.label, detail: `${fmtPeople(c.companies)} companies` })),
      ]));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const q = draft.trim().toLowerCase();
    const shown = opts
      .filter((o) => values.indexOf(o.label) === -1 && (!q || o.label.toLowerCase().includes(q)))
      .sort((a, b) => (q ? Number(!b.label.toLowerCase().startsWith(q)) - Number(!a.label.toLowerCase().startsWith(q)) : 0))
      .slice(0, 12);
    const add = (v: string) => {
      const x = v.trim();
      if (x && values.indexOf(x) === -1) onChange(values.concat([x]));
      setDraft(''); setActive(-1);
    };
    return (
      <div>
        <input
          className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          value={draft}
          placeholder="Pick a region or country, or type a city"
          onChange={(e) => { setDraft(e.target.value); setOpen(true); setActive(-1); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown' && shown.length) { e.preventDefault(); setActive((a) => Math.min(a + 1, shown.length - 1)); }
            else if (e.key === 'ArrowUp' && shown.length) { e.preventDefault(); setActive((a) => Math.max(a - 1, -1)); }
            else if (e.key === 'Enter') { e.preventDefault(); add(active >= 0 && shown[active] ? shown[active].label : draft); }
            else if (e.key === 'Escape') setOpen(false);
          }}
          onBlur={() => { setOpen(false); if (draft.trim()) add(draft); }}
        />
        {open && shown.length > 0 && (
          <div className="mt-1 bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden max-h-64 overflow-y-auto" role="listbox">
            {shown.map((o, i) => (
              <button key={o.label} type="button" role="option" aria-selected={i === active}
                onMouseDown={(e) => { e.preventDefault(); add(o.label); }}
                onMouseEnter={() => setActive(i)}
                className={`w-full flex items-center justify-between gap-2 px-2.5 py-1.5 text-left text-xs ${i === active ? 'bg-gray-100' : 'bg-white'}`}>
                <span className={`truncate ${o.region ? 'font-semibold text-teal-800' : 'text-gray-800'}`}>{o.label}</span>
                <span className="text-gray-400 tabular-nums flex-shrink-0">{o.detail}</span>
              </button>
            ))}
          </div>
        )}
        {values.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {values.map((v) => (
              <span key={v} className="inline-flex items-center gap-1 bg-teal-50 text-teal-800 text-xs px-2 py-0.5 rounded-full">
                {v}
                <button className="opacity-60 hover:opacity-100" onClick={() => onChange(values.filter((x) => x !== v))}>×</button>
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

// Location chips with as-you-type suggestions of real places from the data
// (LinkedIn-style): "lon" → London, England, United Kingdom · 4.8M. Picking
// one adds that exact place; typing your own text and pressing Enter still
// works for "every London".
const fmtPeople = (n: number) => (n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${Math.round(n / 1e3)}k` : String(n));
export const LocationInput: React.FC<{ values: string[]; onChange: (v: string[]) => void; authHeaders: () => Record<string, string>; placeholder?: string; tone?: 'teal' | 'violet' }> =
  ({ values, onChange, authHeaders, placeholder, tone = 'teal' }) => {
    const [draft, setDraft] = useState('');
    const [places, setPlaces] = useState<{ label: string; people: number }[]>([]);
    const [active, setActive] = useState(-1);
    const [open, setOpen] = useState(false);
    const seq = useRef(0);
    const listRef = useRef<HTMLDivElement>(null);
    // The filter panel scrolls: keep the suggestions in view as they arrive.
    useEffect(() => { if (open && places.length) listRef.current?.scrollIntoView({ block: 'nearest' }); }, [open, places]);
    useEffect(() => {
      const q = draft.trim();
      if (q.length < 2) { setPlaces([]); return; }
      const mine = ++seq.current;
      const t = setTimeout(() => {
        fetch(`/api/universe/places?q=${encodeURIComponent(q)}`, { headers: authHeaders() })
          .then((r) => (r.ok ? r.json() : null))
          .then((d) => { if (mine === seq.current) { setPlaces((d && d.places) || []); setActive(-1); } })
          .catch(() => { /* free text still works */ });
      }, 150);
      return () => clearTimeout(t);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draft]);
    const add = (v: string) => {
      const x = v.trim();
      if (x && values.indexOf(x) === -1) onChange(values.concat([x]));
      setDraft(''); setPlaces([]); setActive(-1);
    };
    const ring = tone === 'violet' ? 'focus:ring-violet-500' : 'focus:ring-teal-500';
    const chip = tone === 'violet' ? 'bg-violet-50 text-violet-800' : 'bg-teal-50 text-teal-800';
    return (
      <div>
        <input
          className={`w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 ${ring}`}
          value={draft}
          placeholder={placeholder || 'Start typing a place…'}
          onChange={(e) => { setDraft(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown' && places.length) { e.preventDefault(); setActive((a) => Math.min(a + 1, places.length - 1)); }
            else if (e.key === 'ArrowUp' && places.length) { e.preventDefault(); setActive((a) => Math.max(a - 1, -1)); }
            else if (e.key === 'Enter') { e.preventDefault(); add(active >= 0 && places[active] ? places[active].label : draft); }
            else if (e.key === 'Escape') setOpen(false);
          }}
          onBlur={() => { setOpen(false); if (draft.trim()) add(draft); }}
        />
        {open && places.length > 0 && (
          <div ref={listRef} className="mt-1 bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden" role="listbox">
            {places.map((p, i) => (
              <button
                key={p.label}
                type="button"
                role="option"
                aria-selected={i === active}
                // mousedown, not click: the input's blur would add the typed text first
                onMouseDown={(e) => { e.preventDefault(); add(p.label); }}
                onMouseEnter={() => setActive(i)}
                className={`w-full flex items-center justify-between gap-2 px-2.5 py-1.5 text-left text-xs ${i === active ? 'bg-gray-100' : 'bg-white'}`}
              >
                <span className="text-gray-800 truncate">{p.label}</span>
                <span className="text-gray-400 tabular-nums flex-shrink-0">{(p as any).countries ? `${(p as any).countries} countries` : fmtPeople(p.people)}</span>
              </button>
            ))}
            <div className="px-2.5 py-1 text-[10px] text-gray-400 border-t border-gray-100">Enter to add “{draft.trim()}” as typed (matches every place with that name)</div>
          </div>
        )}
        {values.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {values.map((v) => (
              <span key={v} className={`inline-flex items-center gap-1 ${chip} text-xs px-2 py-0.5 rounded-full`}>
                {v}
                <button className="opacity-60 hover:opacity-100" onClick={() => onChange(values.filter((x) => x !== v))}>×</button>
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

// Collapsible filter group — Apollo-style accordion with an active-count badge.
// ⓘ popover explaining the three title-match modes with worked examples.
export const TitleSyntaxTip: React.FC = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  // Learn tab: "Show me" opens it; opening it (either way) moves the lesson on.
  useLessonAction('search-open-title-tip', () => setOpen(true));
  useEffect(() => { if (open) tourEvent('lesson-title-tip-opened'); }, [open]);
  // In a lesson, close it again once the lesson has moved past the title
  // steps — otherwise it sits over whatever the next step points at.
  const { running: lessonRunning, stepId: lessonStepId } = useTour();
  useEffect(() => {
    if (lessonRunning && lessonStepId && lessonStepId !== 'title-syntax' && lessonStepId !== 'title-syntax-read') setOpen(false);
  }, [lessonRunning, lessonStepId]);
  // Stays open while you read / select its text; a click outside, Esc or ×
  // closes it.
  useEffect(() => {
    if (!open) return;
    // A click on the lesson card (Next, Show me…) is not "outside": closing
    // the panel then would pull the lesson's target away mid-click.
    const away = (e: MouseEvent) => {
      const t = e.target as Element | null;
      if (t && t.closest && t.closest('[data-tour-ui]')) return;
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [open]);
  return (
    <span data-tour="search-title-tip" ref={ref} className="relative inline-flex">
      <button
        type="button"
        aria-label="How title matching works"
        className="w-4 h-4 rounded-full bg-gray-200 text-gray-600 text-[10px] font-bold leading-none hover:bg-gray-300"
        onClick={() => setOpen(!open)}
      >
        i
      </button>
      {open && (
        <div data-tour="search-title-tip-panel" className="absolute left-0 top-5 z-30 w-64 bg-white border border-gray-200 rounded-xl shadow-lg p-3 text-left text-xs text-gray-700 space-y-2 select-text">
          <button type="button" aria-label="Close" className="absolute top-1.5 right-2 text-gray-400 hover:text-gray-700 text-sm leading-none" onClick={() => setOpen(false)}>×</button>
          <p className="font-semibold text-gray-900 pr-4">Three ways to match a job title</p>
          <div>
            <p className="font-medium text-gray-800">sales director <span className="font-normal text-gray-400">— plain words</span></p>
            <p className="text-gray-500">Both words appear anywhere in the title, any order. Matches "Director of Sales", "Regional Sales Director". A word also matches its longer forms (engineer → engineering), except short ones like AI, HR or QA, which match whole words only.</p>
          </div>
          <div>
            <p className="font-medium text-gray-800">"sales director" <span className="font-normal text-gray-400">— quotes</span></p>
            <p className="text-gray-500">The words together, in that order. Matches "Senior Sales Director" but not "Director of Sales".</p>
          </div>
          <div>
            <p className="font-medium text-gray-800">=sales director <span className="font-normal text-gray-400">— equals</span></p>
            <p className="text-gray-500">The whole title is exactly this, nothing more. Matches "Sales Director" only — not "Senior Sales Director".</p>
          </div>
          <p className="text-gray-400">Add several terms — a person matches if ANY of them do. Case never matters.</p>
        </div>
      )}
    </span>
  );
};

// "✨ Suggest related" for a list of job titles: the industry's other names
// for the same person, as chips to click. Nothing changes until one is
// clicked. Suggestions are kept only while the titles they were made for
// are still the list.
export const SuggestRelatedTitles: React.FC<{
  titles: string[]; onAdd: (titles: string[]) => void;
  authHeaders: () => Record<string, string>; context?: string; accent?: 'teal' | 'violet'; tour?: string;
}> = ({ titles, onAdd, authHeaders, context, accent = 'teal', tour }) => {
  const [sug, setSug] = useState<{ sig: string; list: string[] } | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const sigOf = (t: string[]) => t.map((x) => x.toLowerCase()).sort().join('|');
  // Suggestions follow the style of the titles typed (Alex, 4 Nov 2026):
  // "quoted" = exact phrase, =Title = the whole title, plain = words in any
  // order. The style most of the titles use wins (a tie keeps quotes).
  const bare = (t: string) => t.trim().replace(/^=/, '').replace(/^"(.*)"$/, '$1').trim();
  const styleOf = (t: string) => (/^=/.test(t.trim()) ? 'eq' : /^".*"$/.test(t.trim()) ? 'quoted' : 'plain');
  const counts = titles.reduce((c: Record<string, number>, t) => { c[styleOf(t)] = (c[styleOf(t)] || 0) + 1; return c; }, {});
  const style = (counts.eq || 0) > Math.max(counts.quoted || 0, counts.plain || 0) ? 'eq'
    : (counts.quoted || 0) >= (counts.plain || 0) && (counts.quoted || 0) > 0 ? 'quoted' : 'plain';
  const styled = (t: string) => (style === 'eq' ? `=${bare(t)}` : style === 'quoted' ? `"${bare(t)}"` : bare(t));
  const shown = (sug && sug.sig === sigOf(titles) ? sug.list : []).map(styled);
  const link = accent === 'violet' ? 'text-violet-600 hover:text-violet-800' : 'text-teal-600 hover:text-teal-800';
  const suggest = async () => {
    if (!titles.length || loading) return;
    setLoading(true); setErr('');
    try {
      const r = await fetch('/api/universe/suggest-keywords', {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ terms: titles.map(bare), context, kind: 'titles' }),
      });
      const d = await r.json();
      if (!r.ok) setErr(d.error || 'No suggestions');
      else {
        const have = new Set(titles.map((t) => bare(t).toLowerCase()));
        const list: string[] = (d.suggestions || []).map(bare).filter((t: string) => t && !have.has(t.toLowerCase()));
        setSug({ sig: sigOf(titles), list });
        if (!list.length) setErr('Nothing to add — these titles already cover it');
      }
    } catch { setErr('No suggestions'); }
    setLoading(false);
  };
  const take = (t: string[]) => {
    const next = [...titles, ...t.filter((x) => titles.indexOf(x) === -1)];
    onAdd(next);
    const taken = new Set(t.map((x) => bare(x).toLowerCase()));
    setSug({ sig: sigOf(next), list: (sug ? sug.list : []).filter((x) => !taken.has(bare(x).toLowerCase())) });
  };
  if (!titles.length) return null;
  return (
    <div className="space-y-1">
      <button type="button" data-tour={tour} className={`text-[11px] ${link} disabled:opacity-50`} disabled={loading} onClick={suggest}
        title="Other job titles the same person might have">
        {loading ? 'Finding related titles…' : '✨ Suggest related titles'}
      </button>
      {shown.length > 0 && (
        <div className="flex flex-wrap gap-1 items-center">
          {shown.map((t) => (
            <button key={t} type="button" title={`Add "${t}"`}
              className="px-2 py-0.5 rounded-full text-[11px] border border-dashed border-gray-300 text-gray-500 hover:text-gray-800 hover:border-gray-500"
              onClick={() => take([t])}>+ {t}</button>
          ))}
          <button type="button" className={`text-[11px] ${link}`} onClick={() => take(shown)}>Add all</button>
        </div>
      )}
      {err && <div className="text-[11px] text-gray-400">{err}</div>}
    </div>
  );
};

// The Keywords section: groups of words (ANY matches) with the fields each
// group looks in; groups AND together. "✨ Suggest related" offers the
// group's industry equivalents (same rubric as the AI Smart Search) as
// chips to tick — nothing changes the search until one is clicked.
export const KeywordGroupsEditor: React.FC<{
  groups: KeywordGroup[]; onChange: (g: KeywordGroup[]) => void;
  authHeaders: () => Record<string, string>; context?: string; accent?: 'teal' | 'violet';
}> = ({ groups, onChange, authHeaders, context, accent = 'teal' }) => {
  // Suggestions are remembered with the words they were made for, and only
  // shown while the group still holds those words (a removed group or a
  // switched persona never shows another group's suggestions).
  const [suggested, setSuggested] = useState<Record<number, { sig: string; list: string[] }>>({});
  const sigOf = (terms: string[]) => terms.map((t) => t.toLowerCase()).sort().join('|');
  const shown = (i: number) => {
    const x = suggested[i];
    return x && groups[i] && x.sig === sigOf(groups[i].terms) ? x.list : [];
  };
  const [loading, setLoading] = useState<number | null>(null);
  const [err, setErr] = useState<Record<number, string>>({});
  const on = accent === 'violet' ? 'bg-violet-600 text-white border-violet-600' : 'bg-teal-600 text-white border-teal-600';
  const link = accent === 'violet' ? 'text-violet-600 hover:text-violet-800' : 'text-teal-600 hover:text-teal-800';
  const set = (i: number, g: KeywordGroup) => onChange(groups.map((x, j) => (j === i ? g : x)));
  const remove = (i: number) => {
    onChange(groups.filter((_, j) => j !== i));
    setSuggested({}); setErr({});
  };
  const suggest = async (i: number) => {
    const g = groups[i];
    if (!g.terms.length || loading !== null) return;
    setLoading(i); setErr({ ...err, [i]: '' });
    try {
      const r = await fetch('/api/universe/suggest-keywords', {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ terms: g.terms, context }),
      });
      const d = await r.json();
      if (!r.ok) setErr({ ...err, [i]: d.error || 'No suggestions' });
      else {
        const have = new Set(g.terms.map((t) => t.toLowerCase()));
        const list: string[] = (d.suggestions || []).filter((t: string) => !have.has(t.toLowerCase()));
        setSuggested({ ...suggested, [i]: { sig: sigOf(g.terms), list } });
        if (!list.length) setErr({ ...err, [i]: 'Nothing to add — these words already cover it' });
      }
    } catch { setErr({ ...err, [i]: 'No suggestions' }); }
    setLoading(null);
  };
  // Lessons: "Suggest for me" presses the first group's button; an event
  // moves the lesson on once suggestions are showing.
  useLessonAction('search-suggest-keywords', () => suggest(0));
  useEffect(() => { if (shown(0).length) tourEvent('lesson-suggest-shown'); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [suggested]);
  const take = (i: number, terms: string[]) => {
    const g = groups[i];
    const next = [...g.terms, ...terms.filter((t) => g.terms.indexOf(t) === -1)];
    set(i, { ...g, terms: next });
    setSuggested({ ...suggested, [i]: { sig: sigOf(next), list: shown(i).filter((t) => terms.indexOf(t) === -1) } });
  };
  return (
    <div className="space-y-2">
      <div className="text-[11px] text-gray-400">Each group: any of its words, in any ticked field. Groups combine with AND.</div>
      {groups.map((g, i) => (
        <React.Fragment key={i}>
          {i > 0 && <div className="text-[10px] font-semibold text-gray-400 text-center tracking-wide">AND</div>}
          <div className="border border-gray-200 rounded-lg p-2 space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold text-gray-500">Group {i + 1}</span>
              <button type="button" className="text-gray-400 hover:text-gray-700 text-sm leading-none" title="Remove this group" onClick={() => remove(i)}>×</button>
            </div>
            <ChipInput label="" values={g.terms} splitCommas
              excluded={g.exclude} onExcludedChange={(ex, inc) => set(i, { ...g, exclude: ex, terms: inc })}
              onChange={(v) => set(i, { ...g, terms: v })} placeholder="e.g. C#, .NET, ASP.NET (comma = OR)" />
            <div className="flex flex-wrap gap-1">
              {KEYWORD_FIELDS.map(({ key, label }) => {
                const ticked = g.fields.indexOf(key) !== -1;
                return (
                  <button key={key} type="button"
                    className={`px-2 py-0.5 rounded-full text-[11px] border ${ticked ? on : 'bg-white text-gray-500 border-gray-300 hover:border-gray-500'}`}
                    onClick={() => set(i, { ...g, fields: ticked ? g.fields.filter((x) => x !== key) : [...g.fields, key] })}>
                    {ticked ? '✓ ' : ''}{label}
                  </button>
                );
              })}
            </div>
            {!g.fields.length && <div className="text-[11px] text-amber-700">Tick at least one field — this group is ignored until you do.</div>}
            {g.terms.length > 0 && (
              <div className="space-y-1">
                <button type="button" data-tour={i === 0 ? 'search-keyword-suggest' : undefined} className={`text-[11px] ${link} disabled:opacity-50`} disabled={loading !== null} onClick={() => suggest(i)}>
                  {loading === i ? 'Finding related keywords…' : '✨ Suggest related'}
                </button>
                {shown(i).length > 0 && (
                  <div className="flex flex-wrap gap-1 items-center">
                    {shown(i).map((t) => (
                      <button key={t} type="button" title={`Add "${t}" to this group`}
                        className="px-2 py-0.5 rounded-full text-[11px] border border-dashed border-gray-300 text-gray-500 hover:text-gray-800 hover:border-gray-500"
                        onClick={() => take(i, [t])}>+ {t}</button>
                    ))}
                    <button type="button" className={`text-[11px] ${link}`} onClick={() => take(i, shown(i))}>Add all</button>
                  </div>
                )}
                {err[i] && <div className="text-[11px] text-gray-400">{err[i]}</div>}
              </div>
            )}
          </div>
        </React.Fragment>
      ))}
      <button type="button" className={`text-xs ${link}`}
        onClick={() => onChange([...groups, { terms: [], exclude: [], fields: ['headline', 'summary'] }])}>
        + {groups.length ? 'Add another group (AND)' : 'Add keywords'}
      </button>
    </div>
  );
};

// openSignal: bump it to open the section, bring it into view and put the
// cursor in its first input (e.g. "Add a location").
export const FilterSection: React.FC<{ title: string; count?: number; defaultOpen?: boolean; tour?: string; openSignal?: number; children: React.ReactNode }> =
  ({ title, count, defaultOpen, tour, openSignal, children }) => {
    const [open, setOpen] = useState(!!defaultOpen);
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
      if (!openSignal) return;
      setOpen(true);
      setTimeout(() => {
        ref.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        ref.current?.querySelector('input')?.focus();
      }, 50);
    }, [openSignal]);
    return (
      <div data-tour={tour} ref={ref}>
        <button className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-gray-50" onClick={() => setOpen(!open)}>
          <span className="text-[13px] font-medium text-gray-700 flex items-center gap-2">
            {title}
            {count ? (
              <span className="text-[10px] bg-teal-100 text-teal-700 rounded-full px-1.5 py-0.5 font-semibold tabular-nums">{count}</span>
            ) : null}
          </span>
          <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {open && <div className="px-3 pb-3 space-y-2">{children}</div>}
      </div>
    );
  };

// Does a universe search payload narrow by where people are? (top-level or
// in any persona)
export const hasPeopleLocation = (body: any): boolean => {
  const has = (g: any) => !!g && ((Array.isArray(g.locations) && g.locations.length > 0) || !!(g.near && g.near.place));
  return !!body && (has(body) || (Array.isArray(body.orGroups) && body.orGroups.some(has)));
};

// Shown instead of searching when no location is set: a search with no
// location reads everyone in the database, so it is slower.
export const GlobalSearchNotice: React.FC<{ onAddLocation: () => void; onSearchAnyway: () => void; tone?: 'teal' | 'violet' }> =
  ({ onAddLocation, onSearchAnyway, tone = 'teal' }) => (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900 space-y-2" role="alert">
      <div>
        <span className="font-semibold">No location set.</span> This will search everyone in the database
        (US, Europe, Australia &amp; New Zealand), so results can take longer. Adding a location makes it much faster.
      </div>
      <div className="flex gap-2">
        <button className={`flex-1 px-2 py-1.5 rounded-md text-white font-medium ${tone === 'violet' ? 'bg-violet-600 hover:bg-violet-700' : 'bg-teal-600 hover:bg-teal-700'}`}
          onClick={onAddLocation}>Add a location</button>
        <button className="flex-1 px-2 py-1.5 rounded-md bg-white border border-amber-300 text-amber-900 hover:bg-amber-100 font-medium"
          onClick={onSearchAnyway}>Search everywhere</button>
      </div>
    </div>
  );

// Min–max pair for the live search's numeric range filters (0/empty = unset).
export const RangeInput: React.FC<{ label: string; min: string; max: string; onMin: (v: string) => void; onMax: (v: string) => void; maxPlaceholder?: string }> =
  ({ label, min, max, onMin, onMax, maxPlaceholder }) => (
    <div>
      <div className="text-xs font-semibold text-gray-600 mb-1">{label}</div>
      <div className="flex items-center gap-1">
        {/* step="any" — fractional years are real searches (0.2 ≈ 73 days = recent joiners) */}
        <input type="number" min={0} step="any" placeholder="Min" className="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm"
          value={min} onChange={(e) => onMin(e.target.value)} />
        <span className="text-gray-400">–</span>
        <input type="number" min={0} step="any" placeholder={maxPlaceholder || 'Max'} className="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm"
          value={max} onChange={(e) => onMax(e.target.value)} />
      </div>
    </div>
  );

export const ScopeSelect: React.FC<{ value: string; onChange: (v: string) => void; allowPast?: boolean; allowPrimary?: boolean }> =
  ({ value, onChange, allowPast, allowPrimary }) => (
    <select
      className="border border-gray-300 rounded-lg px-2 py-1 text-xs text-gray-600 focus:outline-none"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {/* "Current" includes every live role a person holds; "Primary title
          only" is the strict read of just the headline position. */}
      {allowPrimary && <option value="primary">Primary title only</option>}
      <option value="current">Current{allowPrimary ? ' (any current role)' : ''}</option>
      {allowPast && <option value="past">Past</option>}
      <option value="either">Current or past</option>
    </select>
  );

// universeOnly: the /search page — straight into the universe, no dataset
// chrome (no Sample toggle, no dataset list, no linked-dataset admin). The
// full lab stays at /v2lab for sample/dataset work.

// Industry options taken from the universe itself (value + record count),
// so a filter never offers an industry that matches nothing. Falls back to
// the static list for lab datasets or if the fetch fails.
export type IndustryOptions = { values: string[]; labels: Record<string, string> };
const toOptions = (rows: { value: string; count: number }[], noun: string): IndustryOptions => ({
  values: rows.map((r) => r.value),
  labels: Object.fromEntries(rows.map((r) => [r.value, `${r.count.toLocaleString()} ${noun}`])),
});
// LinkedIn's proficiency scale for the Languages filter's minimum level.
export const LANGUAGE_LEVEL_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Any level' },
  { value: 'ELEMENTARY', label: 'Elementary or better' },
  { value: 'LIMITED_WORKING', label: 'Limited working or better' },
  { value: 'PROFESSIONAL_WORKING', label: 'Professional working or better' },
  { value: 'FULL_PROFESSIONAL', label: 'Full professional or better' },
  { value: 'NATIVE_OR_BILINGUAL', label: 'Native or bilingual' },
];

// Most common language names, for the Languages filter's suggestions.
export function useUniverseLanguages(enabled: boolean, authHeaders: () => Record<string, string>) {
  const [names, setNames] = useState<string[]>([]);
  useEffect(() => {
    if (!enabled || names.length) return;
    fetch('/api/universe/languages', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && Array.isArray(d.languages)) setNames(d.languages); })
      .catch(() => { /* free text still works */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
  return names;
}

// Languages + Certifications filter sections — shared by Search and Contact Search.
export const LanguageCertSections: React.FC<{
  f: SearchFilters; set: (patch: Partial<SearchFilters>) => void; languageNames: string[];
}> = ({ f, set, languageNames }) => (
  <>
    <FilterSection title="Languages" count={f.languages.length}>
      <div className="flex justify-end">
        <select className="text-xs border border-gray-200 rounded-md px-1.5 py-0.5 text-gray-600 bg-white"
          value={f.languageLevel} onChange={(e) => set({ languageLevel: e.target.value })} title="Minimum proficiency">
          {LANGUAGE_LEVEL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      <ChipInput label="" values={f.languages} splitCommas onChange={(v) => set({ languages: v })}
        placeholder="e.g. German, French (any of them)" suggestions={languageNames} suggestionsId="universe-languages" />
      <p className="text-[11px] text-gray-400">About 1 in 10 profiles lists languages{f.languageLevel ? ', and fewer state a level' : ''} — this narrows a lot.</p>
    </FilterSection>
    <FilterSection title="Certifications" count={f.certifications.length}>
      <ChipInput label="" values={f.certifications} splitCommas onChange={(v) => set({ certifications: v })}
        placeholder="e.g. PRINCE2, AWS Certified, CIPD" />
      <p className="text-[11px] text-gray-400">Matches the certificate name or who issued it.</p>
    </FilterSection>
  </>
);

export function useUniverseIndustries(enabled: boolean, authHeaders: () => Record<string, string>) {
  const [opts, setOpts] = useState<{ company: IndustryOptions; role: IndustryOptions } | null>(null);
  useEffect(() => {
    if (!enabled || opts) return;
    (async () => {
      try {
        const r = await fetch('/api/universe/industries', { headers: authHeaders() });
        if (!r.ok) return;
        const d = await r.json();
        if (d.missing) return; // snapshot not generated yet — keep the fallback list
        setOpts({ company: toOptions(d.company || [], 'companies'), role: toOptions(d.role || [], 'roles') });
      } catch { /* keep the fallback */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
  return opts;
}

const UNIVERSE_DS = ({ id: 0, name: 'Universe', kind: 'people', status: 'ready', record_count: 0 } as unknown) as Dataset;

const V2Lab: React.FC<{ universeOnly?: boolean }> = ({ universeOnly }) => {
  const uo = !!universeOnly;
  const { token, user } = useAuth();
  const onTrial = !!(user as any)?.trial;
  const trialLimit: number = (user as any)?.trial?.importLimit || 1000;

  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [selected, setSelected] = useState<Dataset | null>(uo ? UNIVERSE_DS : null);
  const [stats, setStats] = useState<Stats | null>(null);
  // Search is the only view left — Coverage/Table/Map and the vendor-trial
  // enrichment buttons were pre-Forager scaffolding, removed Oct 2026.
  const [tab, setTab] = useState<'coverage' | 'search' | 'table' | 'map'>('search');

  // Search tab
  const [filters, setFilters] = useState<SearchFilters>(EMPTY_FILTERS);
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  const [searchResults, setSearchResults] = useState<PersonRow[]>([]);
  const [searchTotal, setSearchTotal] = useState<number | null>(null);
  const [searchPage, setSearchPage] = useState(0);
  const [searching, setSearching] = useState(false);
  // People | Companies search mode, grouped-by-company view, drill-down
  const [searchMode, setSearchMode] = useState<'people' | 'companies'>('people');
  // Data source: the selected sample dataset (Postgres) or the Forager
  // universe (ClickHouse). Same filters, same UI — different compiler.
  const [source, setSource] = useState<'dataset' | 'universe'>('universe');
  // The animated loader: only after 400ms of searching, fades out on results.
  const loaderPhase = useSearchLoaderPhase(searching && source === 'universe' && searchPage === 0);
  const industryOpts = useUniverseIndustries(source === 'universe', () => ({ Authorization: `Bearer ${token}` }));
  const languageNames = useUniverseLanguages(source === 'universe', () => ({ Authorization: `Bearer ${token}` }));
  const [universeStatus, setUniverseStatus] = useState<null | { people: number; companies: number; error?: string; importLimit?: number | null; importLimitPlan?: string; importTierKey?: string | null; monthlyImports?: { used: number; limit: number; left: number } | null }>(null);
  const [ignoredFilters, setIgnoredFilters] = useState<string[]>([]);
  // Opt-in personas (universe people search): several complete searches
  // OR-combined, names become tags on import. `filters` always holds the
  // ACTIVE persona's filters; the others park in personaFilters.
  const [personasEnabled, setPersonasEnabled] = useState(false);
  const [personaMeta, setPersonaMeta] = useState<{ name: string }[]>([{ name: 'Persona 1' }]);
  const [personaFilters, setPersonaFilters] = useState<SearchFilters[]>([EMPTY_FILTERS]);
  // Each persona also carries its own COMPANY filters, so personas work the
  // same in People and Companies mode (companyFilters = the active persona's).
  const [personaCompanyFilters, setPersonaCompanyFilters] = useState<CompanyFilters[]>([EMPTY_COMPANY_FILTERS]);
  const [activePersona, setActivePersona] = useState(0);
  const [groupCounts, setGroupCounts] = useState<number[] | null>(null);
  const [companyGroupCounts, setCompanyGroupCounts] = useState<number[] | null>(null);
  // Natural-language search builder: the prompt becomes ordinary editable
  // filters (variants arrive as named personas), then the search runs.
  const [buildPrompt, setBuildPrompt] = useState('');
  const [building, setBuilding] = useState(false);
  const [buildNote, setBuildNote] = useState<string | null>(null);
  const [buildGaps, setBuildGaps] = useState<string[]>([]);
  const [groupMode, setGroupMode] = useState(false);
  const [groupRows, setGroupRows] = useState<any[] | null>(null);
  const [drill, setDrill] = useState<null | { name: string; keepFilters: boolean }>(null);
  const [companyFilters, setCompanyFilters] = useState<CompanyFilters>(EMPTY_COMPANY_FILTERS);
  const [companyResults, setCompanyResults] = useState<any[]>([]);
  const [companyTotal, setCompanyTotal] = useState<number | null>(null);
  const [companyPage, setCompanyPage] = useState(0);
  // Bulk company entry (paste / CSV) — feeds people-filter companies or the
  // companies-mode list, depending on which opened it.
  const [bulkOpen, setBulkOpen] = useState<null | 'people' | 'companies'>(null);
  const [bulkText, setBulkText] = useState('');
  const [addName, setAddName] = useState('');
  // Where results go: '' = a new project named addName, else an existing project's id.
  const [addTarget, setAddTarget] = useState('');
  const [myProjects, setMyProjects] = useState<{ id: number; name: string }[]>([]);
  const [addingToProject, setAddingToProject] = useState(false);
  const [linkChoice, setLinkChoice] = useState('');
  const [linking, setLinking] = useState(false);
  const [linkInfo, setLinkInfo] = useState<any | null>(null);

  // Companies dataset table
  const [compList, setCompList] = useState<CompanyRow[]>([]);
  const [compTotal, setCompTotal] = useState(0);
  const [loading, setLoading] = useState(!uo);
  const [error, setError] = useState('');


  // Upload
  const [uploadKind, setUploadKind] = useState<'people' | 'companies'>('people');
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Table
  const [people, setPeople] = useState<PersonRow[]>([]);
  const [peopleTotal, setPeopleTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [tableLoading, setTableLoading] = useState(false);

  // Detail drawer
  const [detail, setDetail] = useState<any | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Map
  const mapRef = useRef<google.maps.Map | null>(null);
  const clustererRef = useRef<MarkerClusterer | null>(null);
  const [geoPoints, setGeoPoints] = useState<any[]>([]);
  const [geoLoaded, setGeoLoaded] = useState(false);
  const [geocodeConfirm, setGeocodeConfirm] = useState(false);

  // Promote to real project
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [promoteName, setPromoteName] = useState('');
  const [promoting, setPromoting] = useState(false);

  // Mapping editor
  const [editingMapping, setEditingMapping] = useState(false);
  const [mappingDraft, setMappingDraft] = useState<Record<string, string>>({});
  const [savingMapping, setSavingMapping] = useState(false);

  const authHeaders = useCallback((): Record<string, string> => ({ Authorization: `Bearer ${token}` }), [token]);

  // ---- Learn tab lesson ("Find your market and map it") -------------------
  // While the lesson runs, Search works on a fixed example (server:
  // services/lessonSearch.js): the prompt types itself, Build search and
  // Search answer instantly, and Add to project opens the user's copy of
  // the shared sample project. Nothing here runs outside the lesson.
  const { running: runningLesson, stepId: lessonStep } = useTour();
  const inLesson = uo && runningLesson === 'search-to-map';
  const [lessonData, setLessonData] = useState<any>(null);
  useEffect(() => {
    if (!inLesson || lessonData) return;
    fetch('/api/universe/lesson/search-to-map', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setLessonData(d); })
      .catch(() => { /* lesson falls back to live behaviour */ });
  }, [inLesson, lessonData, authHeaders]);
  useEffect(() => {
    if (inLesson && lessonData) setAddName(lessonData.projectName);
  }, [inLesson, lessonData]);

  useLessonAction('search-type-prompt', () => {
    const text: string = lessonData?.prompt || 'AI engineers in Denmark';
    let i = 0;
    setBuildPrompt('');
    const t = window.setInterval(() => {
      i += 1;
      setBuildPrompt(text.slice(0, i));
      if (i >= text.length) { window.clearInterval(t); tourEvent('lesson-prompt-typed'); }
    }, 55);
  });
  useLessonAction('search-remove-title', () => {
    if (!lessonData) return;
    setFilters((f) => ({ ...f, titles: f.titles.filter((x) => x.toLowerCase() !== lessonData.removeTitle) }));
  });
  useLessonAction('search-add-title', () => {
    if (!lessonData) return;
    setFilters((f) => (f.titles.some((x) => x.toLowerCase() === lessonData.addTitle) ? f : { ...f, titles: [...f.titles, lessonData.addTitle] }));
  });
  // The user can also remove/add the tags by hand — move on either way.
  useEffect(() => {
    if (!inLesson || !lessonData) return;
    const has = filters.titles.map((x) => x.toLowerCase());
    if (lessonStep === 'remove-title' && !has.includes(lessonData.removeTitle)) tourEvent('lesson-title-removed');
    if (lessonStep === 'add-title' && has.includes(lessonData.addTitle)) tourEvent('lesson-title-added');
  }, [filters.titles, inLesson, lessonData, lessonStep]);
  // Keywords step: a group of words looked for in the headline and summary.
  const [keywordSignal, setKeywordSignal] = useState(0);
  const LESSON_KEYWORDS = ['generative ai', 'llm'];
  useLessonAction('search-add-keyword-group', () => {
    setFilters((f) => (f.keywordGroups.some((g) => g.terms.length)
      ? f
      : { ...f, keywordGroups: [...f.keywordGroups, { terms: LESSON_KEYWORDS, exclude: [], fields: ['headline', 'summary'] }] }));
    setKeywordSignal((n) => n + 1);
  });
  useEffect(() => {
    if (inLesson && lessonStep === 'keywords' && filters.keywordGroups.some((g) => g.terms.length)) tourEvent('lesson-keyword-added');
  }, [filters.keywordGroups, inLesson, lessonStep]);
  useLessonAction('search-enable-personas', () => { if (!personasEnabled) addPersona(); });
  useLessonAction('search-duplicate-persona', () => duplicatePersona());

  const buildLessonSearch = () => {
    if (!lessonData) { setError('The lesson example is still loading — try again in a moment.'); return; }
    setBuilding(true); setBuildNote(null); setError('');
    window.setTimeout(() => {
      const f = builtToFilters(lessonData.built);
      setPersonaMeta([{ name: 'AI engineers' }]);
      setPersonaFilters([f]);
      setPersonaCompanyFilters([companyFilters]);
      setActivePersona(0);
      setFilters(f);
      setPersonasEnabled(false);
      setBuildNote('Built 1 search from your description');
      setSearchResults([]); setSearchTotal(null); setGroupRows(null);
      setBuilding(false);
      tourEvent('search-built');
    }, 900);
  };

  const runLessonSearch = () => {
    setDrill(null);
    setSearching(true);
    setSearchPage(0);
    window.setTimeout(() => {
      setSearchResults(lessonData.people || []);
      setSearchTotal(lessonData.total || 0);
      setIgnoredFilters([]);
      setGroupCounts(null);
      setSearching(false);
      tourEvent('search-results');
    }, 1500);
  };

  const addLessonProject = async () => {
    setAddingToProject(true);
    try {
      const resp = await fetch('/api/universe/lesson/search-to-map/project', { method: 'POST', headers: authHeaders() });
      const data = await resp.json();
      if (!resp.ok) { setError(data.error || 'Could not open the sample project'); setAddingToProject(false); return; }
      tourEvent('search-import-started');
      // Already has the sample project → straight there; else the normal import flow.
      setPromoteJob(data.existing
        ? { projectId: data.projectId, projectName: data.projectName, phase: 'candidates', total: 0, inserted: 0, done: true, error: null }
        : { ...data, phase: 'candidates', inserted: 0, done: false, error: null });
    } catch {
      setError('Could not open the sample project');
      setAddingToProject(false);
    }
  };

  const fetchDatasets = useCallback(async () => {
    try {
      const resp = await fetch('/api/v2lab/datasets', { headers: authHeaders() });
      if (resp.ok) {
        const data = await resp.json();
        setDatasets(data.datasets || []);
      }
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, [authHeaders]);

  useEffect(() => { if (!uo) fetchDatasets(); }, [fetchDatasets, uo]);

  const fetchDatasetDetail = useCallback(async (id: number): Promise<Dataset | null> => {
    try {
      const resp = await fetch(`/api/v2lab/datasets/${id}`, { headers: authHeaders() });
      if (!resp.ok) return null;
      const data = await resp.json();
      return data.dataset;
    } catch (e) { return null; }
  }, [authHeaders]);

  const fetchStats = useCallback(async (id: number) => {
    try {
      const resp = await fetch(`/api/v2lab/datasets/${id}/stats`, { headers: authHeaders() });
      if (resp.ok) setStats(await resp.json());
    } catch (e) { /* ignore */ }
  }, [authHeaders]);

  const fetchCompanies = useCallback(async (id: number, pageNum: number, q: string) => {
    setTableLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(pageNum * PAGE_SIZE) });
      if (q) params.set('q', q);
      const resp = await fetch(`/api/v2lab/datasets/${id}/companies?${params.toString()}`, { headers: authHeaders() });
      if (resp.ok) {
        const d = await resp.json();
        setCompList(d.companies || []);
        setCompTotal(d.total || 0);
      }
    } catch (e) { /* ignore */ }
    setTableLoading(false);
  }, [authHeaders]);

  const fetchPeople = useCallback(async (id: number, pageNum: number, q: string) => {
    setTableLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(pageNum * PAGE_SIZE) });
      if (q) params.set('q', q);
      const resp = await fetch(`/api/v2lab/datasets/${id}/people?${params.toString()}`, { headers: authHeaders() });
      if (resp.ok) {
        const data = await resp.json();
        setPeople(data.people || []);
        setPeopleTotal(data.total || 0);
      }
    } catch (e) { /* ignore */ }
    setTableLoading(false);
  }, [authHeaders]);

  const openDataset = async (ds: Dataset, initialTab?: 'coverage' | 'search' | 'table' | 'map') => {
    setSelected(ds);
    setStats(null);
    setPeople([]);
    setPage(0);
    setSearch('');
    setActiveSearch('');
    setDetail(null);
    setGeoPoints([]);
    setGeoLoaded(false);
    setEditingMapping(false);
    setFilters(EMPTY_FILTERS);
    setSearchResults([]);
    setSearchTotal(null);
    setSearchPage(0);
    setAddName('');
    setTab('search');
    setLinkChoice('');
    setLinkInfo(null);
    setCompList([]);
    setCompTotal(0);
    const full = await fetchDatasetDetail(ds.id);
    if (full) setSelected(full);
    if (full && full.status === 'ready') {
      fetchStats(ds.id);
      if (full.kind === 'companies') fetchCompanies(ds.id, 0, '');
      else fetchPeople(ds.id, 0, '');
    }
  };

  // Poll while the dataset is importing / geocoding / re-mapping.
  useEffect(() => {
    if (!selected || (selected.status !== 'importing' && selected.status !== 'geocoding')) return;
    const iv = setInterval(async () => {
      const full = await fetchDatasetDetail(selected.id);
      if (!full) return;
      setSelected(full);
      if (full.status === 'ready') {
        fetchStats(full.id);
        if (full.kind === 'companies') fetchCompanies(full.id, 0, activeSearch);
        else fetchPeople(full.id, 0, activeSearch);
        setGeoLoaded(false);
        fetchDatasets();
      } else if (full.status === 'importing' && full.kind !== 'companies' && Number(full.record_count) > 0) {
        // Live-filling fetch: refresh the table as pages arrive mid-import.
        fetchPeople(full.id, 0, activeSearch);
      }
    }, 2500);
    return () => clearInterval(iv);
  }, [selected, fetchDatasetDetail, fetchStats, fetchPeople, fetchCompanies, fetchDatasets, activeSearch]);

  // Table paging / search
  useEffect(() => {
    if (selected && selected.status === 'ready' && selected.id > 0) {
      if (selected.kind === 'companies') fetchCompanies(selected.id, page, activeSearch);
      else fetchPeople(selected.id, page, activeSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, activeSearch]);

  // ---- Upload ----
  const doUpload = (file: File) => {
    if (!file) return;
    setError('');
    setUploadPct(0);
    const form = new FormData();
    form.append('file', file);
    form.append('kind', uploadKind);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/v2lab/datasets');
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) setUploadPct(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = async () => {
      setUploadPct(null);
      if (xhr.status >= 200 && xhr.status < 300) {
        const data = JSON.parse(xhr.responseText);
        await fetchDatasets();
        if (data.dataset) openDataset(data.dataset);
      } else {
        try { setError(JSON.parse(xhr.responseText).error || 'Upload failed'); }
        catch { setError('Upload failed'); }
      }
    };
    xhr.onerror = () => { setUploadPct(null); setError('Upload failed — network error'); };
    xhr.send(form);
  };

  const handleDelete = async (ds: Dataset) => {
    if (!window.confirm(`Delete dataset "${ds.name}" and all its imported records? Your real projects are not affected.`)) return;
    await fetch(`/api/v2lab/datasets/${ds.id}`, { method: 'DELETE', headers: authHeaders() });
    if (selected?.id === ds.id) setSelected(null);
    fetchDatasets();
  };

  // ---- Link a companies dataset to this people dataset ----
  const handleLink = async () => {
    if (!selected || !linkChoice) return;
    setLinking(true);
    try {
      const resp = await fetch(`/api/v2lab/datasets/${selected.id}/link-companies`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyDatasetId: parseInt(linkChoice) }),
      });
      const data = await resp.json();
      if (resp.ok) {
        setLinkInfo(data.link);
        const full = await fetchDatasetDetail(selected.id);
        if (full) setSelected(full);
        fetchDatasets();
      } else {
        setError(data.error || 'Failed to link datasets');
      }
    } catch (e) { setError('Failed to link datasets'); }
    setLinking(false);
  };

  const openCompanyDetail = async (companyId: number) => {
    if (!selected) return;
    setDetailLoading(true);
    setDetail({ id: companyId, __company: true });
    try {
      const resp = await fetch(`/api/v2lab/datasets/${selected.id}/companies/${companyId}`, { headers: authHeaders() });
      if (resp.ok) {
        const data = await resp.json();
        setDetail({ ...data.company, __company: true });
      }
    } catch (e) { /* ignore */ }
    setDetailLoading(false);
  };

  // ---- Detail drawer ----
  const openDetail = async (personId: number) => {
    if (!selected) return;
    setDetailLoading(true);
    setDetail({ id: personId });
    try {
      const resp = await fetch(
        source === 'universe' ? `/api/universe/person/${personId}` : `/api/v2lab/datasets/${selected.id}/people/${personId}`,
        { headers: authHeaders() });
      if (resp.ok) {
        const data = await resp.json();
        setDetail(data.person);
      }
    } catch (e) { /* ignore */ }
    setDetailLoading(false);
  };

  // ---- Map ----
  const loadGeo = useCallback(async () => {
    if (!selected) return;
    try {
      const resp = await fetch(`/api/v2lab/datasets/${selected.id}/geo`, { headers: authHeaders() });
      if (resp.ok) {
        const data = await resp.json();
        setGeoPoints(data.points || []);
        setGeoLoaded(true);
      }
    } catch (e) { /* ignore */ }
  }, [selected, authHeaders]);

  useEffect(() => {
    if (tab === 'map' && selected && selected.status === 'ready' && !geoLoaded) loadGeo();
  }, [tab, selected, geoLoaded, loadGeo]);

  // (Re)build markers whenever points change and the map is mounted.
  const rebuildMarkers = useCallback((map: google.maps.Map | null, points: any[]) => {
    if (clustererRef.current) {
      clustererRef.current.clearMarkers();
      clustererRef.current = null;
    }
    if (!map || !points.length) return;
    const markers = points.map((p) => {
      const m = new google.maps.Marker({
        position: { lat: Number(p.latitude), lng: Number(p.longitude) },
        title: p.name || '',
      });
      m.addListener('click', () => openDetail(p.id));
      return m;
    });
    clustererRef.current = new MarkerClusterer({ map, markers });
    const bounds = new google.maps.LatLngBounds();
    points.forEach((p) => bounds.extend({ lat: Number(p.latitude), lng: Number(p.longitude) }));
    map.fitBounds(bounds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);

  useEffect(() => {
    if (tab === 'map') rebuildMarkers(mapRef.current, geoPoints);
    return () => {
      if (clustererRef.current) { clustererRef.current.clearMarkers(); clustererRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geoPoints, tab]);

  const startGeocode = async () => {
    if (!selected) return;
    setGeocodeConfirm(false);
    const resp = await fetch(`/api/v2lab/datasets/${selected.id}/geocode`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ limit: 10000 }),
    });
    if (resp.ok) {
      const full = await fetchDatasetDetail(selected.id);
      if (full) setSelected(full);
    } else {
      try { setError((await resp.json()).error || 'Failed to start geocoding'); } catch { setError('Failed to start geocoding'); }
    }
  };

  // ---- Search ----
  const filtersPayload = (f: SearchFilters) => ({
    keywordGroups: keywordGroupsPayload(f),
    titles: f.titles, titleScope: f.titleScope, locations: f.locations, headlineKeywords: f.headlineKeywords,
    summaryKeywords: f.summaryKeywords, anywhereKeywords: f.anywhereKeywords, skills: f.skills,
    roleKeywords: f.roleKeywords, roleScope: f.roleScope,
    employmentTypes: expandEmploymentGroups(f.employmentTypes), employmentScope: f.employmentScope,
    industries: f.industries, industryScope: f.industryScope,
    companies: f.companies, companyScope: f.companyScope,
    schools: f.schools, degrees: f.degrees,
    tenureMin: f.tenureMin || undefined, tenureMax: f.tenureMax || undefined,
    joinedCompanyMin: f.joinedMin || undefined, joinedCompanyMax: f.joinedMax || undefined,
    experienceMin: f.experienceMin || undefined, experienceMax: f.experienceMax || undefined,
    languages: f.languages, languageLevel: f.languageLevel || undefined, certifications: f.certifications,
    near: nearPayload(f),
    ...notPayload(f),
  });

  // ONE filter model across both tabs: whatever is set on the Companies tab
  // rides along as a predicate on every people search (and vice versa — the
  // companies the user listed on the People side are visible there too).
  const allPersonaFilters = (): SearchFilters[] => {
    const arr = [...personaFilters];
    arr[activePersona] = filters;
    return arr;
  };
  const allPersonaCompanyFilters = (): CompanyFilters[] => {
    const arr = personaMeta.map((_, i) => personaCompanyFilters[i] || EMPTY_COMPANY_FILTERS);
    arr[activePersona] = companyFilters;
    return arr;
  };
  // Personas with any filters, paired with their names (orGroups ↔ groupNames
  // stay aligned). People mode: people filters AND that persona's company
  // filters. Companies mode: only personas with company filters count.
  const activePersonaPairs = () => {
    const cfs = allPersonaCompanyFilters();
    return allPersonaFilters()
      .map((f, i) => ({ f, cf: cfs[i] || EMPTY_COMPANY_FILTERS, name: personaMeta[i]?.name || `Persona ${i + 1}` }))
      .filter((p) => countPeopleFilters(p.f) > 0 || countCompanyFilters(p.cf) > 0);
  };
  // Search templates (SearchTemplates.tsx): every persona's people and
  // company filters, their names, and the describe-it prompt.
  const templateState = () => ({
    v: 1,
    personasEnabled,
    personaMeta,
    personaFilters: allPersonaFilters(),
    personaCompanyFilters: allPersonaCompanyFilters(),
    activePersona,
    buildPrompt,
  });
  const applyTemplateState = (t: any) => {
    const pf: SearchFilters[] = (Array.isArray(t.personaFilters) && t.personaFilters.length ? t.personaFilters : [EMPTY_FILTERS])
      .map((f: any) => ({ ...EMPTY_FILTERS, ...f }));
    const pcf: CompanyFilters[] = pf.map((_, i) => ({ ...EMPTY_COMPANY_FILTERS, ...((t.personaCompanyFilters || [])[i] || {}) }));
    const meta = pf.map((_, i) => ({ name: (t.personaMeta || [])[i]?.name || `Persona ${i + 1}` }));
    const a = Math.min(Math.max(0, t.activePersona || 0), pf.length - 1);
    setPersonaMeta(meta);
    setPersonaFilters(pf);
    setPersonaCompanyFilters(pcf);
    setActivePersona(a);
    setFilters(pf[a]);
    setCompanyFilters(pcf[a]);
    setPersonasEnabled(!!t.personasEnabled && pf.length > 1);
    setBuildPrompt(t.buildPrompt || '');
    drillFiltersRef.current = null;
    setDrill(null);
    setSearchResults([]); setSearchTotal(null); setGroupRows(null);
    setCompanyResults([]); setCompanyTotal(null);
  };
  const activeCompanyPersonaPairs = () => activePersonaPairs().filter((p) => countCompanyFilters(p.cf) > 0);
  const switchPersona = (i: number) => {
    if (i === activePersona) return;
    const arr = allPersonaFilters();
    const carr = allPersonaCompanyFilters();
    setPersonaFilters(arr);
    setPersonaCompanyFilters(carr);
    setActivePersona(i);
    setFilters(arr[i]);
    setCompanyFilters(carr[i] || EMPTY_COMPANY_FILTERS);
  };
  const addPersona = () => {
    if (!personasEnabled) { setPersonasEnabled(true); tourEvent('lesson-personas-on'); return; }
    if (personaMeta.length >= 6) return;
    const arr = allPersonaFilters();
    const carr = allPersonaCompanyFilters();
    setPersonaFilters([...arr, EMPTY_FILTERS]);
    setPersonaCompanyFilters([...carr, EMPTY_COMPANY_FILTERS]);
    setPersonaMeta([...personaMeta, { name: `Persona ${personaMeta.length + 1}` }]);
    setActivePersona(arr.length);
    setFilters(EMPTY_FILTERS);
    setCompanyFilters(EMPTY_COMPANY_FILTERS);
  };
  // A copy of the persona being edited — tweak one thing (e.g. move its
  // headline keywords to the summary) without rebuilding the rest.
  const duplicatePersona = () => {
    if (personaMeta.length >= 6) return;
    const arr = allPersonaFilters();
    const carr = allPersonaCompanyFilters();
    const copy: SearchFilters = JSON.parse(JSON.stringify(arr[activePersona] || EMPTY_FILTERS));
    const ccopy: CompanyFilters = JSON.parse(JSON.stringify(carr[activePersona] || EMPTY_COMPANY_FILTERS));
    setPersonaFilters([...arr, copy]);
    setPersonaCompanyFilters([...carr, ccopy]);
    setPersonaMeta([...personaMeta, { name: `${personaMeta[activePersona]?.name || 'Persona'} (copy)` }]);
    setActivePersona(arr.length);
    setFilters(copy);
    setCompanyFilters(ccopy);
    tourEvent('lesson-persona-duplicated');
  };
  const removePersona = (i: number) => {
    if (personaMeta.length === 1) return;
    const arr = allPersonaFilters().filter((_, j) => j !== i);
    const carr = allPersonaCompanyFilters().filter((_, j) => j !== i);
    const meta = personaMeta.filter((_, j) => j !== i);
    const next = Math.max(0, Math.min(i < activePersona ? activePersona - 1 : activePersona, arr.length - 1));
    setPersonaFilters(arr);
    setPersonaCompanyFilters(carr);
    setPersonaMeta(meta);
    setActivePersona(next);
    setFilters(arr[next]);
    setCompanyFilters(carr[next] || EMPTY_COMPANY_FILTERS);
  };

  // Builder output → a full SearchFilters object (strings/arrays normalized).
  // AI / saved output → editable filters, per-field keywords as groups.
  const builtToFilters = (f: any): SearchFilters => withKeywordGroups({
    ...builtToFiltersRaw(f),
    ...(f.near && f.near.place ? { nearPlace: String(f.near.place), nearMiles: String(f.near.miles || 25) } : {}),
    keywordGroups: (Array.isArray(f.keywordGroups) ? f.keywordGroups : []).map((g: any) => ({
      terms: g.terms || [], exclude: g.exclude || [], fields: g.fields || [],
    })),
  });
  const builtToFiltersRaw = (f: any): SearchFilters => ({
    ...EMPTY_FILTERS,
    titles: f.titles || [], locations: f.locations || [], skills: f.skills || [],
    headlineKeywords: f.headlineKeywords || [], summaryKeywords: f.summaryKeywords || [],
    anywhereKeywords: f.anywhereKeywords || [],
    roleKeywords: f.roleKeywords || [], employmentTypes: f.employmentTypes || [],
    industries: f.industries || [], companies: f.companies || [],
    schools: f.schools || [], degrees: f.degrees || [],
    titleScope: f.titleScope || 'current', roleScope: f.roleScope || 'current',
    employmentScope: f.employmentScope || 'current', industryScope: f.industryScope || 'current',
    companyScope: f.companyScope || 'current',
    tenureMin: f.tenureMin != null ? String(f.tenureMin) : '',
    tenureMax: f.tenureMax != null ? String(f.tenureMax) : '',
    joinedMin: f.joinedCompanyMin != null ? String(f.joinedCompanyMin) : '',
    joinedMax: f.joinedCompanyMax != null ? String(f.joinedCompanyMax) : '',
    experienceMin: f.experienceMin != null ? String(f.experienceMin) : '',
    experienceMax: f.experienceMax != null ? String(f.experienceMax) : '',
    languages: f.languages || [], languageLevel: f.languageLevel || '', certifications: f.certifications || [],
    titlesExclude: f.titlesExclude || [], skillsExclude: f.skillsExclude || [],
    headlineKeywordsExclude: f.headlineKeywordsExclude || [], employmentSignals: !!f.employmentSignals,
  });

  // Company-side output (companyQuery / employer size) lands in the shared
  // company filters, which already ride every people search as a predicate.
  const applyBuiltCompanyQuery = (ps: any[]): CompanyFilters | null => {
    const src = ps.map((p) => p.filters).find((f) => f.companyQuery || f.headcountMin != null || f.headcountMax != null || f.foundedAfter != null || f.foundedBefore != null);
    if (!src) return null;
    const cq = src.companyQuery || {};
    return {
      ...EMPTY_COMPANY_FILTERS,
      industries: cq.industries || [], nameKeywords: cq.nameKeywords || [],
      locations: cq.locations || [], descriptionKeywords: cq.descriptionKeywords || [],
      keywords: cq.keywords || [], technologies: cq.technologies || [],
      headcountMin: (cq.headcountMin ?? src.headcountMin) != null ? String(cq.headcountMin ?? src.headcountMin) : '',
      headcountMax: (cq.headcountMax ?? src.headcountMax) != null ? String(cq.headcountMax ?? src.headcountMax) : '',
      foundedAfter: (cq.foundedAfter ?? src.foundedAfter) != null ? String(cq.foundedAfter ?? src.foundedAfter) : '',
      foundedBefore: (cq.foundedBefore ?? src.foundedBefore) != null ? String(cq.foundedBefore ?? src.foundedBefore) : '',
    };
  };

  const buildSearch = async () => {
    if (!buildPrompt.trim() || building) return;
    if (inLesson) { buildLessonSearch(); return; }
    setBuilding(true); setBuildNote(null); setError('');
    try {
      const resp = await fetch('/api/universe/build-search', {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: buildPrompt.trim() }),
      });
      const d = await resp.json();
      if (!resp.ok) { setError(d.error || 'Could not build the search'); setBuilding(false); return; }
      const ps: { name: string; filters: any }[] = d.personas || [];
      const fList = ps.map((p) => builtToFilters(p.filters));
      const builtCompany = applyBuiltCompanyQuery(ps);
      if (builtCompany) setCompanyFilters(builtCompany);
      const cqCount = builtCompany
        ? builtCompany.industries.length + builtCompany.nameKeywords.length + builtCompany.locations.length +
          builtCompany.descriptionKeywords.length + builtCompany.keywords.length + builtCompany.technologies.length +
          (builtCompany.headcountMin ? 1 : 0) + (builtCompany.headcountMax ? 1 : 0) +
          (builtCompany.foundedAfter ? 1 : 0) + (builtCompany.foundedBefore ? 1 : 0)
        : 0;

      setPersonaMeta(ps.map((p) => ({ name: p.name })));
      setPersonaFilters(fList);
      setPersonaCompanyFilters(fList.map(() => builtCompany || companyFilters));
      setActivePersona(0);
      setFilters(fList[0]);
      setPersonasEnabled(ps.length > 1);
      if (d.note) setBuildNote(d.note);
      setBuildGaps(Array.isArray(d.notApplied) ? d.notApplied : []);
      drillFiltersRef.current = null;
      setDrill(null);
      // Fill the filters only — the user reviews them and presses Search.
      // Auto-running spent a full universe scan on drafts that often
      // needed a tweak first. Clear old results so they aren't mistaken
      // for this draft's.
      void cqCount;
      setSearchResults([]); setSearchTotal(null); setGroupRows(null);
      tourEvent('search-built');
    } catch (e) {
      setError('Could not build the search');
    }
    setBuilding(false);
  };

  // "Search" on the projects list (/search?fromProject=ID): load the search
  // a project was built from — personas and company filters included — to
  // edit and run again, then add to a new or an existing project.
  const [loadedFrom, setLoadedFrom] = useState<null | { projectId: number; projectName: string; searches: any[]; index: number }>(null);
  // Opened from a project: hide people already in it (on by default — the
  // point is growing that market map) and badge any that show.
  const [onlyNewToProject, setOnlyNewToProject] = useState(true);
  const onlyNewRef = useRef(true); // read by runSearch so a toggle re-runs with the new value at once
  const [projectCtx, setProjectCtx] = useState<null | { projectId: number; projectName: string; inProject: number; onlyNew: boolean; matchesAlreadyIn?: number }>(null);
  const companyQueryToFilters = (cq: any, src: any = {}): CompanyFilters => ({
    ...EMPTY_COMPANY_FILTERS,
    industries: cq.industries || [], nameKeywords: cq.nameKeywords || [],
    locations: cq.locations || [], descriptionKeywords: cq.descriptionKeywords || [],
    keywords: cq.keywords || [], technologies: cq.technologies || [], companiesList: cq.companiesList || [],
    headcountMin: (cq.headcountMin ?? src.headcountMin) != null ? String(cq.headcountMin ?? src.headcountMin) : '',
    headcountMax: (cq.headcountMax ?? src.headcountMax) != null ? String(cq.headcountMax ?? src.headcountMax) : '',
    foundedAfter: (cq.foundedAfter ?? src.foundedAfter) != null ? String(cq.foundedAfter ?? src.foundedAfter) : '',
    foundedBefore: (cq.foundedBefore ?? src.foundedBefore) != null ? String(cq.foundedBefore ?? src.foundedBefore) : '',
  });
  const applySavedSearch = (saved: { filters: any; groupNames?: string[] | null; prompt?: string | null }) => {
    const { orGroups, ...top } = saved.filters || {};
    // Top-level filters apply to every persona (searches made through Claude can carry both).
    const groups: any[] = Array.isArray(orGroups) && orGroups.length ? orGroups.map((g: any) => ({ ...top, ...g })) : [top];
    const names = saved.groupNames || [];
    const fList = groups.map((g) => builtToFilters({ ...g, employmentTypes: collapseEmploymentGroups(g.employmentTypes || []) }));
    const cList = groups.map((g) => (g.companyQuery || g.headcountMin != null || g.headcountMax != null
      ? companyQueryToFilters(g.companyQuery || {}, g) : EMPTY_COMPANY_FILTERS));
    setPersonaMeta(groups.map((_, i) => ({ name: names[i] || `Persona ${i + 1}` })));
    setPersonaFilters(fList);
    setPersonaCompanyFilters(cList);
    setActivePersona(0);
    setFilters(fList[0]);
    setCompanyFilters(cList[0]);
    setPersonasEnabled(groups.length > 1);
    if (saved.prompt) setBuildPrompt(saved.prompt);
    setSource('universe'); setTab('search'); setSearchMode('people');
    drillFiltersRef.current = null;
    setDrill(null);
    setSearchResults([]); setSearchTotal(null); setGroupRows(null);
  };
  useEffect(() => {
    const pid = parseInt(new URLSearchParams(window.location.search).get('fromProject') || '', 10);
    if (!pid || inLesson) return;
    // Drop the parameter so a refresh doesn't overwrite later edits.
    try { window.history.replaceState(null, '', window.location.pathname); } catch { /* ignore */ }
    fetch(`/api/universe/project-search/${pid}`, { headers: authHeaders() })
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (!ok) { setError(d.error || 'Could not load the project\'s search'); return; }
        if (!d.searches || !d.searches.length) { setError(`“${d.projectName}” wasn't built from a search we kept, so there's nothing to load.`); return; }
        applySavedSearch(d.searches[0]);
        setLoadedFrom({ projectId: d.projectId, projectName: d.projectName, searches: d.searches, index: 0 });
        setAddTarget(String(d.projectId)); // "Add to project" defaults to the project it came from
      })
      .catch(() => setError('Could not load the project\'s search'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const usingPersonas = personasEnabled && source === 'universe';
  const peoplePayload = () => (usingPersonas
    ? {
      // Each persona = its people filters AND its own company filters.
      orGroups: activePersonaPairs().map((p) => ({
        ...filtersPayload(p.f),
        ...(countCompanyFilters(p.cf) > 0 ? { companyQuery: companyFiltersPayload(p.cf) } : {}),
      })),
    }
    : {
      ...filtersPayload(filters),
      ...(companyFilterCount > 0 ? { companyQuery: companyFiltersPayload(companyFilters) } : {}),
    });

  // Drill-down ("show all employees at X"): when set, searches run with
  // these filters instead of the panel's, leaving the panel state intact.
  const drillFiltersRef = useRef<any>(null);

  // Universe (ClickHouse) reachability + row counts, fetched on first switch.
  useEffect(() => {
    if (source !== 'universe' || universeStatus) return;
    (async () => {
      try {
        const resp = await fetch('/api/universe/status', { headers: authHeaders() });
        const d = await resp.json();
        setUniverseStatus(resp.ok ? d : { people: 0, companies: 0, error: d.error || 'Universe unreachable' });
      } catch {
        setUniverseStatus({ people: 0, companies: 0, error: 'Universe unreachable' });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  // Results belong to one source — clear them when switching.
  const switchSource = (s: 'dataset' | 'universe') => {
    if (s === source) return;
    setSource(s);
    setSearchResults([]); setSearchTotal(null); setGroupRows(null);
    setCompanyResults([]); setCompanyTotal(null);
    setDrill(null); drillFiltersRef.current = null; setIgnoredFilters([]);
  };

  // Ask before a search with no location (see GlobalSearchNotice).
  const [globalAsk, setGlobalAsk] = useState(false);
  const [locationSignal, setLocationSignal] = useState(0);
  const globalOkRef = useRef(false);
  const startPeopleSearch = () => {
    setGlobalAsk(false);
    setDrill(null);
    drillFiltersRef.current = null;
    if (groupMode) { runGrouped(); } else { setGroupRows(null); runSearch(0); }
  };

  const runSearch = async (page: number, overrideBody?: any) => {
    if (!selected) return;
    if (inLesson && page === 0 && lessonData) { runLessonSearch(); return; }
    setSearching(true);
    setSearchPage(page);
    scrollToResultsOnPhone('[data-tour="search-results"]');
    try {
      const body = overrideBody || drillFiltersRef.current || peoplePayload();
      const resp = await fetch(source === 'universe' ? '/api/universe/search' : `/api/v2lab/datasets/${selected.id}/search`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filters: body, limit: PAGE_SIZE, offset: page * PAGE_SIZE, skipCount: page > 0,
          ...(loadedFrom && source === 'universe' ? { projectId: loadedFrom.projectId, onlyNew: onlyNewRef.current } : {}),
        }),
      });
      if (resp.ok) {
        const d = await resp.json();
        setSearchResults(d.people || []);
        setProjectCtx(d.projectContext || null);
        // total: null on page flips means "unchanged" (count pass skipped)
        if (d.total != null) setSearchTotal(d.total);
        setIgnoredFilters(d.ignored || []);
        if (d.total != null) setGroupCounts(d.groupCounts || null);
        if (page === 0 && d.total != null) tourEvent('search-results');
      } else {
        try { setError((await resp.json()).error || 'Search failed'); } catch (e) { setError('Search failed'); }
      }
    } catch (e) { setError('Search failed'); }
    setSearching(false);
  };

  // Grouped ("find people in these companies") view.
  const runGrouped = async () => {
    if (!selected) return;
    setSearching(true);
    setGroupRows(null);
    try {
      const resp = await fetch(source === 'universe' ? '/api/universe/search/by-company' : `/api/v2lab/datasets/${selected.id}/search/by-company`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ filters: peoplePayload() }),
      });
      if (resp.ok) {
        const d = await resp.json();
        setGroupRows(d.companies || []);
        setSearchTotal(null);
        setSearchResults([]);
        setDrill(null);
        drillFiltersRef.current = null;
      } else {
        try { setError((await resp.json()).error || 'Grouped search failed'); } catch (e) { setError('Grouped search failed'); }
      }
    } catch (e) { setError('Grouped search failed'); }
    setSearching(false);
  };

  // Click a company in the grouped view: matched>0 keeps the panel filters;
  // a 0 (or the employee count) lifts them so EVERY employee is listed and
  // the user can pick individually.
  const drillIntoCompany = (row: any, keepFilters: boolean) => {
    const entry = row.entry || row.domain || row.linkedin_url || row.name;
    if (!entry) return;
    drillFiltersRef.current = keepFilters
      ? { ...peoplePayload(), companies: [entry], companyScope: 'current' }
      : { companies: [entry], companyScope: 'current' };
    setDrill({ name: row.name || entry, keepFilters });
    setGroupRows(null);
    runSearch(0);
  };

  const exitDrill = () => {
    drillFiltersRef.current = null;
    setDrill(null);
    setSearchResults([]);
    setSearchTotal(null);
    if (groupMode) runGrouped();
  };

  // ---- Companies search mode ----
  const companyTargetId = selected?.kind === 'companies' ? selected.id : selected?.linked_dataset_id;

  const companyFiltersPayload = (f: CompanyFilters) => ({
    nameKeywords: f.nameKeywords, industries: f.industries, locations: f.locations,
    headcountMin: f.headcountMin || undefined, headcountMax: f.headcountMax || undefined,
    foundedAfter: f.foundedAfter || undefined, foundedBefore: f.foundedBefore || undefined,
    descriptionKeywords: f.descriptionKeywords, keywords: f.keywords,
    technologies: f.technologies, companiesList: f.companiesList,
  });

  // Companies mode: with personas, a company matches when ANY persona's
  // company filters match (e.g. "CDMO" in description OR in specialties).
  const companySearchPayload = () => (usingPersonas && activeCompanyPersonaPairs().length > 0
    ? { orGroups: activeCompanyPersonaPairs().map((p) => companyFiltersPayload(p.cf)) }
    : companyFiltersPayload(companyFilters));

  // Persona chips + name — the same personas in People and Companies mode.
  const personaBar = usingPersonas ? (

                  <div className="px-3 pt-2.5 pb-2 border-b border-gray-100">
                    <div className="flex flex-wrap gap-1.5">
                      {personaMeta.map((m, i) => {
                        const c = PERSONA_COLORS[i % PERSONA_COLORS.length];
                        const f = i === activePersona ? filters : personaFilters[i];
                        const cf = i === activePersona ? companyFilters : personaCompanyFilters[i];
                        const n = (f ? countPeopleFilters(f) : 0) + (cf ? countCompanyFilters(cf) : 0);
                        return (
                          <button
                            key={i}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${i === activePersona ? `${c.bg} ${c.text} border-transparent` : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'}`}
                            onClick={() => switchPersona(i)}
                            title={`Edit ${m.name}`}
                          >
                            <span className={`w-2 h-2 rounded-full ${c.solid}`} />
                            {m.name}
                            {n > 0 && <span className="tabular-nums opacity-70">({n})</span>}
                            {personaMeta.length > 1 && (
                              <span role="button" className="opacity-50 hover:opacity-100" title={`Remove ${m.name}`}
                                onClick={(e) => { e.stopPropagation(); removePersona(i); }}>×</span>
                            )}
                          </button>
                        );
                      })}
                      {personaMeta.length < 6 && (
                        <button className="px-2.5 py-1 rounded-full text-xs border border-dashed border-gray-300 text-gray-400 hover:text-gray-700 hover:border-gray-500"
                          onClick={addPersona} title="Add another persona — results combine anyone matching any persona">
                          + persona
                        </button>
                      )}
                      {personaMeta.length < 6 && (
                        <button data-tour="search-persona-duplicate" className="px-2.5 py-1 rounded-full text-xs border border-dashed border-gray-300 text-gray-400 hover:text-gray-700 hover:border-gray-500"
                          onClick={duplicatePersona} title={`Copy ${personaMeta[activePersona]?.name || 'this persona'} with all its filters`}>
                          ⧉ duplicate
                        </button>
                      )}
                    </div>
                    <input
                      className="mt-2 w-full border border-gray-200 rounded-lg px-2 py-1 text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-teal-400"
                      value={personaMeta[activePersona]?.name || ''}
                      onChange={(e) => setPersonaMeta(personaMeta.map((m, i) => (i === activePersona ? { name: e.target.value } : m)))}
                      placeholder="Persona name (e.g. Engineering Directors)"
                    />
                  </div>
  ) : null;

  const runCompanySearch = async (page: number) => {
    if (source !== 'universe' && !companyTargetId) return;
    setSearching(true);
    setCompanyPage(page);
    scrollToResultsOnPhone('[data-mobile-results="companies"]');
    try {
      const resp = await fetch(source === 'universe' ? '/api/universe/search-companies' : `/api/v2lab/datasets/${companyTargetId}/search-companies`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ filters: companySearchPayload(), limit: PAGE_SIZE, offset: page * PAGE_SIZE }),
      });
      if (resp.ok) {
        const d = await resp.json();
        setCompanyResults(d.companies || []);
        setCompanyTotal(d.total || 0);
        setCompanyGroupCounts(Array.isArray(d.groupCounts) ? d.groupCounts : null);
      } else {
        try { setError((await resp.json()).error || 'Company search failed'); } catch (e) { setError('Company search failed'); }
      }
    } catch (e) { setError('Company search failed'); }
    setSearching(false);
  };

  // Import runs as a backend job; this opens the progress modal and the
  // poll effect below tracks it through candidates → companies → done.
  const [promoteJob, setPromoteJob] = useState<any | null>(null);

  // Projects the results can be added to (own + team), most recently active first.
  useEffect(() => {
    fetch('/api/projects', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => {
        if (!Array.isArray(rows)) return;
        const at = (p: any) => new Date(p.last_activity_at || p.updated_at || p.created_at || 0).getTime();
        setMyProjects([...rows].sort((a: any, b: any) => at(b) - at(a)).map((p: any) => ({ id: p.id, name: p.name })));
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const addTargetReady = !!addTarget || !!addName.trim();
  const addTargetBody = () => (addTarget ? { projectId: Number(addTarget) } : { name: addName.trim() });
  const addTargetName = addTarget ? (myProjects.find((p) => String(p.id) === addTarget)?.name || 'project') : null;
  const projectTargetPicker = (width: string) => (
    <>
      {!inLesson && myProjects.length > 0 && (
        <select
          className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm max-w-[14rem] focus:outline-none focus:ring-2 focus:ring-teal-500"
          value={addTarget}
          onChange={(e) => setAddTarget(e.target.value)}
          disabled={addingToProject}
          title="Add to a new project or one you already have"
        >
          <option value="">New project…</option>
          <optgroup label="Add to existing project">
            {myProjects.map((p) => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
          </optgroup>
        </select>
      )}
      {!addTarget && (
        <input
          className={`border border-gray-300 rounded-lg px-3 py-1.5 text-sm ${width} focus:outline-none focus:ring-2 focus:ring-teal-500`}
          placeholder="New project name"
          value={addName}
          onChange={(e) => setAddName(e.target.value)}
          disabled={addingToProject}
          readOnly={inLesson}
        />
      )}
    </>
  );

  const addResultsToProject = async () => {
    if (!selected || !addTargetReady) return;
    if (inLesson) { addLessonProject(); return; }
    setAddingToProject(true);
    try {
      const resp = await fetch(source === 'universe' ? '/api/universe/promote' : `/api/v2lab/datasets/${selected.id}/promote`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...addTargetBody(), filters: peoplePayload(),
          ...(usingPersonas ? { groupNames: activePersonaPairs().map((p) => p.name) } : {}),
          // The words the recruiter started from (hiring manager auto-discovery reads them).
          ...(buildPrompt.trim() ? { prompt: buildPrompt.trim() } : {}),
        }),
      });
      const data = await resp.json();
      if (resp.ok) {
        setPromoteJob({ ...data, phase: 'candidates', inserted: 0, done: false, error: null, what: 'people' });
        tourEvent('search-import-started');
      } else {
        setError(data.error || 'Failed to create project');
        setAddingToProject(false);
      }
    } catch (e) {
      setError('Failed to create project');
      setAddingToProject(false);
    }
  };

  // Companies-only import: a project of just the matched companies, to
  // research first and source people into later.
  const addCompaniesOnlyToProject = async () => {
    if ((source !== 'universe' && !companyTargetId) || !addTargetReady) return;
    setAddingToProject(true);
    try {
      const resp = await fetch(source === 'universe' ? '/api/universe/promote-companies' : `/api/v2lab/datasets/${companyTargetId}/promote-companies`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...addTargetBody(), filters: companySearchPayload(),
          ...(usingPersonas ? { groupNames: activeCompanyPersonaPairs().map((p) => p.name) } : {}) }),
      });
      const data = await resp.json();
      if (resp.ok) {
        setPromoteJob({ ...data, phase: 'companies', total: 0, inserted: 0, done: false, error: null, what: 'companies' });
      } else {
        setError(data.error || 'Failed to create project');
        setAddingToProject(false);
      }
    } catch (e) {
      setError('Failed to create project');
      setAddingToProject(false);
    }
  };

  useEffect(() => {
    if (!promoteJob?.jobId || promoteJob.done) return;
    const t = setInterval(async () => {
      try {
        // Universe imports report through the universe route (open to
        // search testers); lab-dataset imports through the lab route.
        const resp = await fetch(
          source === 'universe' ? `/api/universe/promote-status/${promoteJob.jobId}` : `/api/v2lab/promote-status/${promoteJob.jobId}`,
          { headers: authHeaders() });
        if (resp.status === 404) {
          // Backend restarted mid-import — the job is gone; say so rather
          // than spinning forever.
          setPromoteJob((p: any) => p ? { ...p, done: true, error: 'The import was interrupted (server restarted). Check the project — it may be partial.' } : p);
          return;
        }
        if (!resp.ok) return;
        const d = await resp.json();
        setPromoteJob((p: any) => (p ? { ...p, ...d } : p));
      } catch { /* transient poll failure — keep trying */ }
    }, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoteJob?.jobId, promoteJob?.done]);

  // Done and clean → open the project.
  useEffect(() => {
    if (promoteJob?.done && !promoteJob.error) {
      tourEvent('search-import-done');
      // Free trial: say what the trial limit did (or, on a first import, what
      // the paid plan adds) — shown on the project page this navigates to.
      if (onTrial) {
        if (promoteJob.limited) promptAfterReload({ reason: 'import', to: promoteJob.limited.to, of: promoteJob.limited.of, what: promoteJob.what });
        else if (!localStorage.getItem('trialFirstImportPrompted')) {
          try { localStorage.setItem('trialFirstImportPrompted', '1'); } catch { /* ignore */ }
          promptAfterReload({ reason: 'first' });
        }
      }
      const t = setTimeout(() => { releaseLeaveGuard(); window.location.assign(`/project/${promoteJob.projectId}`); }, 900);
      return () => clearTimeout(t);
    }
  }, [promoteJob?.done, promoteJob?.error, promoteJob?.projectId]);

  // ---- Mapping editor ----
  const startEditMapping = () => {
    const draft: Record<string, string> = {};
    const m = selected?.mapping || {};
    CONCEPTS.forEach(({ key }) => {
      const v = (m as any)[key];
      draft[key] = Array.isArray(v) ? v.join(', ') : (v || '');
    });
    setMappingDraft(draft);
    setEditingMapping(true);
  };

  const saveMapping = async () => {
    if (!selected) return;
    setSavingMapping(true);
    const mapping: Record<string, string | string[]> = {};
    Object.keys(mappingDraft).forEach((k) => {
      const v = mappingDraft[k].trim();
      if (!v) return;
      const parts = v.split(',').map((s) => s.trim()).filter(Boolean);
      mapping[k] = parts.length > 1 ? parts : parts[0];
    });
    const resp = await fetch(`/api/v2lab/datasets/${selected.id}/mapping`, {
      method: 'PUT',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ mapping }),
    });
    setSavingMapping(false);
    if (resp.ok) {
      setEditingMapping(false);
      const full = await fetchDatasetDetail(selected.id);
      if (full) setSelected(full);   // status flips to importing → polling takes over
    } else {
      setError('Failed to save mapping');
    }
  };

  // ---- Render helpers ----
  const pct = (num: any, den: any) => {
    const n = Number(num) || 0, d = Number(den) || 0;
    return d ? Math.round((n / d) * 100) : 0;
  };

  const covColor = (p: number) => (p >= 60 ? 'bg-emerald-500' : p >= 20 ? 'bg-amber-500' : 'bg-red-400');
  const covBadge = (p: number) =>
    p >= 60 ? 'bg-emerald-50 text-emerald-700' : p >= 20 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-600';

  const fieldStatEntries = () => {
    const fs = selected?.field_stats || {};
    const total = selected?.record_count || 1;
    return Object.keys(fs)
      .map((path) => ({ path, ...fs[path], coverage: pct(fs[path].count, total) }))
      .sort((a, b) => b.coverage - a.coverage);
  };

  const mappingLabel = (key: string) => {
    const v = (selected?.mapping as any)?.[key];
    if (!v) return null;
    return Array.isArray(v) ? v.join(' + ') : v;
  };

  const busy = selected && (selected.status === 'importing' || selected.status === 'geocoding');

  const activeFilterCount = countPeopleFilters(filters);

  const companyFilterCount = countCompanyFilters(companyFilters);

  // =========================================================================

  if (loading) {
    return <div className="p-10 text-gray-400">Loading V2 Lab…</div>;
  }

  // ---- Dataset list / landing ----
  if (!selected) {
    return (
      <div className="p-6 max-w-5xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">V2 Lab</h1>
          <p className="text-gray-500 mt-1">
            Sandbox for purchased candidate datasets. Everything here is stored separately —
            nothing touches your projects, candidates or clients.
          </p>
        </div>

        {/* Search preview — how the future "Search" dashboard tab will open:
            straight into People | Companies search over the universe, with
            none of the dataset management below (that all disappears once
            the Forager feed lands in ClickHouse). */}
        {(() => {
          const ready = datasets.filter((d) => d.kind !== 'companies' && d.status === 'ready')
            .sort((a, b) => Number(b.record_count || 0) - Number(a.record_count || 0));
          const primary = ready.find((d) => d.linked_dataset_id) || ready[0];
          if (!primary) return null;
          const linked = datasets.find((d) => d.id === primary.linked_dataset_id);
          return (
            <button
              className="w-full mb-6 text-left bg-gradient-to-r from-teal-600 to-cyan-600 rounded-xl px-5 py-4 text-white shadow hover:from-teal-700 hover:to-cyan-700 transition-colors flex items-center gap-4"
              onClick={() => openDataset(primary, 'search')}
            >
              <span className="text-2xl">🔍</span>
              <span className="flex-1">
                <span className="block text-base font-semibold">Open Search</span>
                <span className="block text-xs text-teal-50/90 mt-0.5">
                  Preview of the future Search tab — People &amp; Companies filters over {primary.name}
                  {linked ? ` + ${linked.name}` : ''}. The dataset admin below won't exist in the real thing.
                </span>
              </span>
              <span className="text-xl">→</span>
            </button>
          );
        })()}

        <div className="flex items-center gap-2 mb-3">
          <span className="text-sm text-gray-500">Dataset type:</span>
          {(['people', 'companies'] as const).map((k) => (
            <button
              key={k}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize border ${uploadKind === k ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-600 border-gray-300 hover:border-teal-400'}`}
              onClick={() => setUploadKind(k)}
            >
              {k}
            </button>
          ))}
          {uploadKind === 'companies' && (
            <span className="text-xs text-gray-400">Company records — linked to people datasets by LinkedIn URL/domain to power company filters.</span>
          )}
        </div>
        <div
          className={`border-2 border-dashed rounded-xl p-10 text-center transition-colors cursor-pointer ${dragOver ? 'border-teal-500 bg-teal-50' : 'border-gray-300 bg-white hover:border-teal-400'}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            if (e.dataTransfer.files && e.dataTransfer.files[0]) doUpload(e.dataTransfer.files[0]);
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".ndjson,.jsonl,.json,.txt"
            className="hidden"
            onChange={(e) => { if (e.target.files && e.target.files[0]) doUpload(e.target.files[0]); e.target.value = ''; }}
          />
          {uploadPct !== null ? (
            <div>
              <div className="text-gray-700 font-medium mb-2">Uploading… {uploadPct}%</div>
              <div className="w-64 h-2 bg-gray-200 rounded-full mx-auto overflow-hidden">
                <div className="h-full bg-teal-500 rounded-full transition-all" style={{ width: `${uploadPct}%` }} />
              </div>
            </div>
          ) : (
            <>
              <div className="text-gray-800 font-semibold text-lg">Drop a dataset file here</div>
              <div className="text-gray-500 text-sm mt-1">.ndjson, .jsonl or .json — one candidate record per line</div>
              <div className="text-gray-400 text-xs mt-3">Fields are auto-detected on import; you can re-map them afterwards.</div>
            </>
          )}
        </div>

        {error && <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{linkifyError(error)}</div>}


        {datasets.length > 0 && (
          <div className="mt-8">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Datasets</h2>
            <div className="space-y-2">
              {datasets.map((ds) => (
                <div key={ds.id} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between hover:border-teal-400 transition-colors">
                  <button className="text-left flex-1" onClick={() => openDataset(ds)}>
                    <div className="font-semibold text-gray-900 flex items-center gap-2">
                      {ds.name}
                      {ds.kind === 'companies' && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 font-semibold uppercase tracking-wide">Companies</span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500 mt-0.5">
                      {ds.status === 'importing' ? (ds.status_detail || 'Importing…')
                        : ds.status === 'failed' ? `Failed — ${ds.status_detail || 'unknown error'}`
                        : `${Number(ds.record_count).toLocaleString()} records · ${Number(ds.geocoded_count || 0).toLocaleString()} geocoded`}
                      {' · '}{new Date(ds.created_at).toLocaleDateString()}
                    </div>
                  </button>
                  <div className="flex items-center gap-3 ml-4">
                    {ds.status !== 'ready' && ds.status !== 'failed' && (
                      <span className="text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-600 animate-pulse">{ds.status}</span>
                    )}
                    <button className="text-gray-400 hover:text-red-500 text-sm" onClick={() => handleDelete(ds)}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ---- Dataset workspace ----
  const cov = stats?.coverage;
  const total = Number(cov?.total) || selected.record_count || 0;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="px-6 py-3 border-b border-gray-200 bg-white flex items-center gap-4 flex-wrap">
        {uo ? (
          <>
            <div className="font-semibold text-gray-900">Search</div>
            <div className="text-sm text-gray-500">People &amp; companies across the live universe</div>
          </>
        ) : (
          <>
            <button className="text-gray-400 hover:text-gray-700 text-sm" onClick={() => { setSelected(null); fetchDatasets(); }}>
              ← Datasets
            </button>
            <div className="font-semibold text-gray-900">{selected.name}</div>
            <div className="text-sm text-gray-500">
              {Number(selected.record_count).toLocaleString()} records
              {Number(selected.error_count) > 0 && ` · ${selected.error_count} unparseable lines`}
            </div>
          </>
        )}
        {busy && (
          <span className="text-xs px-2.5 py-1 rounded-full bg-blue-50 text-blue-600 animate-pulse">
            {selected.status_detail || selected.status}
          </span>
        )}
        {selected.status === 'failed' && (
          <span className="text-xs px-2.5 py-1 rounded-full bg-red-50 text-red-600" title={selected.status_detail || ''}>
            Failed — {selected.status_detail || 'unknown error'}
          </span>
        )}
        <div className="ml-auto flex items-center gap-3">
          {!uo && selected.status === 'ready' && selected.kind !== 'companies' && !promoteOpen && (
            <button
              className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700"
              onClick={() => { setPromoteName(selected.name); setPromoteOpen(true); }}
            >
              Create project →
            </button>
          )}
          {promoteOpen && (
            <div className="flex items-center gap-2">
              <input
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-teal-500"
                value={promoteName}
                onChange={(e) => setPromoteName(e.target.value)}
                placeholder="Project name"
                disabled={promoting}
              />
              <button
                className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50"
                disabled={promoting || !promoteName.trim()}
                onClick={async () => {
                  if (!selected) return;
                  setPromoting(true);
                  try {
                    const resp = await fetch(`/api/v2lab/datasets/${selected.id}/promote`, {
                      method: 'POST',
                      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                      body: JSON.stringify({ name: promoteName.trim() }),
                    });
                    const data = await resp.json();
                    if (resp.ok) {
                      releaseLeaveGuard(); // added to a project: opening it isn't leaving unsaved work
                      window.location.assign(`/project/${data.projectId}`);
                    } else {
                      setError(data.error || 'Failed to create project');
                      setPromoting(false);
                    }
                  } catch (e) {
                    setError('Failed to create project');
                    setPromoting(false);
                  }
                }}
              >
                {promoting ? 'Creating…' : 'Go'}
              </button>
              {!promoting && (
                <button className="text-sm text-gray-500 hover:text-gray-800" onClick={() => setPromoteOpen(false)}>Cancel</button>
              )}
            </div>
          )}
        </div>
      </div>

      {loadedFrom && (
        <div className="mx-6 mt-3 p-3 bg-teal-50 text-teal-900 rounded-lg text-sm flex items-center gap-3 flex-wrap">
          <span>
            Loaded the search from <a href={`/project/${loadedFrom.projectId}`} className="font-medium underline">{loadedFrom.projectName}</a>
            {' '}— edit the filters and press Search, then add the results to a new project or an existing one.
          </span>
          {loadedFrom.searches.length > 1 && (
            <select
              className="border border-teal-200 bg-white rounded px-2 py-1 text-xs"
              value={loadedFrom.index}
              onChange={(e) => {
                const i = Number(e.target.value);
                applySavedSearch(loadedFrom.searches[i]);
                setLoadedFrom({ ...loadedFrom, index: i });
              }}
              title="This project has had more than one search added to it"
            >
              {loadedFrom.searches.map((sv, i) => (
                <option key={i} value={i}>
                  {i === 0 ? 'Latest search' : `Earlier search ${i}`}{sv.at ? ` — ${new Date(sv.at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
                </option>
              ))}
            </select>
          )}
          <label className="flex items-center gap-1.5 text-xs basis-full sm:basis-auto">
            <input
              type="checkbox"
              checked={onlyNewToProject}
              onChange={(e) => {
                onlyNewRef.current = e.target.checked;
                setOnlyNewToProject(e.target.checked);
                if (searchTotal !== null) runSearch(0);
              }}
            />
            Only show people not already in {loadedFrom.projectName}
            {projectCtx && projectCtx.onlyNew && projectCtx.inProject > 0 && (
              <span className="text-teal-700">(the {projectCtx.inProject.toLocaleString()} people already in it are left out)</span>
            )}
          </label>
          <button
            className="text-xs underline text-teal-800 hover:text-teal-950"
            title="Re-run this project's saved searches every week and add people who newly match (tagged New for 30 days). Also on the projects list."
            onClick={async () => {
              const r = await fetch(`/api/projects/${loadedFrom.projectId}/auto-refresh`, {
                method: 'PUT', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ enabled: true }),
              }).catch(() => null);
              const d = r ? await r.json().catch(() => ({})) : {};
              if (r && r.ok) setBuildNote(`Keep growing is on for ${loadedFrom.projectName}: every week it re-runs its saved searches and adds people who newly match${d.scope && d.scope.fixed ? ` (at its ${d.scope.companies} companies)` : ''}. Switch it off on the projects list.`);
              else setError((d && d.error) || 'Could not switch on Keep growing');
            }}
          >
            Keep this project growing weekly
          </button>
          <button onClick={() => { setLoadedFrom(null); setProjectCtx(null); }} className="ml-auto text-teal-500 hover:text-teal-800">✕</button>
        </div>
      )}

      {error && (
        <div className="mx-6 mt-3 p-3 bg-red-50 text-red-700 rounded-lg text-sm flex justify-between">
          <span>{linkifyError(error)}</span>
          <button onClick={() => setError('')} className="text-red-400 hover:text-red-700 ml-4">✕</button>
        </div>
      )}

      <div className="flex-1 overflow-hidden flex">
        <div className="flex-1 overflow-y-auto">

          {/* ---- Search tab ---- */}
          {tab === 'search' && (
            <div className="p-4 space-y-3">
              {/* People | Companies mode toggle (companies datasets are companies-only) */}
              <div className="flex items-center gap-3 flex-wrap">
              {selected.kind !== 'companies' && (
                <div data-tour="search-mode-toggle" className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit">
                  {(['people', 'companies'] as const).map((m) => {
                    const n = m === 'people' ? activeFilterCount : companyFilterCount;
                    return (
                      <button
                        key={m}
                        className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${searchMode === m ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}
                        onClick={() => setSearchMode(m)}
                        title={m === 'companies' ? 'Company filters set here carry over to People automatically' : 'People filters stay applied while you adjust company filters'}
                      >
                        {m === 'people' ? 'People' : 'Companies'}
                        {n > 0 && (
                          <span className="text-[10px] bg-teal-600 text-white rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center font-semibold tabular-nums">{n}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              {/* Sample dataset vs live universe — lab surface only */}
              {!uo && (
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit">
                {(['dataset', 'universe'] as const).map((s) => (
                  <button
                    key={s}
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${source === s ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}
                    onClick={() => switchSource(s)}
                    title={s === 'universe' ? 'Search the full universe — the live dataset' : 'Search the uploaded sample dataset'}
                  >
                    {s === 'dataset' ? 'Sample' : 'Universe'}
                  </button>
                ))}
              </div>
              )}
              {source === 'universe' && universeStatus && (
                universeStatus.error
                  ? <span className="text-xs text-red-500">{universeStatus.error}</span>
                  : <span className="text-xs text-gray-500 tabular-nums">
                      {Number(universeStatus.people).toLocaleString()} candidates · {Number(universeStatus.companies).toLocaleString()} companies in the universe
                    </span>
              )}
              {ignoredFilters.length > 0 && (
                <span className="text-xs text-amber-600" title="These filters have no universe column yet and were not applied">
                  ⚠ not applied in universe: {ignoredFilters.join(', ')}
                </span>
              )}
              </div>
              {/* Saved templates (+ the "save before you leave?" prompt) */}
              {source === 'universe' && !inLesson && (
                <SearchTemplatesBar
                  page="search"
                  hasFilters={activePersonaPairs().length > 0}
                  getState={templateState}
                  applyState={applyTemplateState}
                  baselineKey={loadedFrom ? `project-${loadedFrom.projectId}-${loadedFrom.index}` : undefined}
                />
              )}
              {/* Describe-it search: a sentence becomes editable filters
                  (variants as named personas) and the search runs. */}
              {source === 'universe' && searchMode === 'people' && (
                <div data-tour="search-describe" className="bg-white rounded-xl border border-gray-200 px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="text-base" aria-hidden>✨</span>
                    <input
                      className="flex-1 border-0 text-sm focus:outline-none placeholder:text-gray-400"
                      placeholder={`Describe who you're looking for — e.g. "HV engineers in Denmark" or "marketing leaders at fintechs over 100 staff, 2+ years in role"`}
                      value={buildPrompt}
                      onChange={(e) => setBuildPrompt(e.target.value)}
                      onPaste={(e) => pasteListIntoPrompt(e, buildPrompt, setBuildPrompt)}
                      onKeyDown={(e) => { if (e.key === 'Enter') buildSearch(); }}
                      disabled={building}
                      readOnly={inLesson}
                    />
                    <button
                      className="px-3 py-1.5 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-50 whitespace-nowrap"
                      disabled={building || !buildPrompt.trim()}
                      onClick={buildSearch}
                    >
                      {building ? 'Building…' : 'Build search'}
                    </button>
                  </div>
                  {buildNote && (
                    <div className="mt-1.5 text-xs text-gray-500 flex items-start gap-2">
                      <span className="flex-1">{buildNote} — review the filters on the left, then press Search.</span>
                      <button className="text-gray-300 hover:text-gray-600" onClick={() => setBuildNote(null)}>✕</button>
                    </div>
                  )}
                  <BuildGaps items={buildGaps} />
                </div>
              )}
              {(searchMode === 'people' && selected.kind !== 'companies') ? (
              <div className="flex gap-4 items-start max-md:flex-col max-md:items-stretch">
              {/* Filter rail (collapsed) / panel */}
              {filtersCollapsed ? (
                <button
                  data-tour="search-filters"
                  className="w-11 flex-shrink-0 bg-white rounded-xl border border-gray-200 py-3 flex flex-col items-center gap-2 hover:border-teal-400 max-md:w-full max-md:flex-row max-md:justify-center max-md:py-2"
                  onClick={() => setFiltersCollapsed(false)}
                  title="Show filters"
                >
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                  {activeFilterCount > 0 && (
                    <span className="text-[10px] bg-teal-600 text-white rounded-full w-5 h-5 flex items-center justify-center font-semibold tabular-nums">{activeFilterCount}</span>
                  )}
                </button>
              ) : (
              <div data-tour="search-filters" className="w-72 flex-shrink-0 bg-white rounded-xl border border-gray-200 flex flex-col max-h-[calc(100vh-150px)] max-md:w-full max-md:max-h-none">
                <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-100">
                  <span className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                    Filters
                    {activeFilterCount > 0 && (
                      <span className="text-[10px] bg-teal-600 text-white rounded-full px-1.5 py-0.5 font-semibold tabular-nums">{activeFilterCount}</span>
                    )}
                  </span>
                  <div className="flex items-center gap-1">
                    {source === 'universe' && !personasEnabled && (
                      <button
                        data-tour="search-personas"
                        className="text-xs text-teal-700 hover:text-teal-900 px-1"
                        title="Run several searches at once (e.g. Engineering Directors + data science OR any Talent Acquisition) and combine the results — persona names become tags on import"
                        onClick={addPersona}
                      >
                        + Personas
                      </button>
                    )}
                    {activeFilterCount > 0 && (
                      <button className="text-xs text-gray-400 hover:text-gray-700 px-1" onClick={() => { setFilters(EMPTY_FILTERS); setSearchResults([]); setSearchTotal(null); }}>
                        Clear all
                      </button>
                    )}
                    <button className="text-gray-400 hover:text-gray-700 p-1" title="Collapse filters" onClick={() => setFiltersCollapsed(true)}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                      </svg>
                    </button>
                  </div>
                </div>
                {personaBar}
                <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
                  <FilterSection title="Job titles" count={filters.titles.length + filters.titlesExclude.length} defaultOpen tour="search-filter-titles">
                    <div className="flex justify-end">
                      <ScopeSelect value={filters.titleScope} allowPast allowPrimary
                        onChange={(v) => setFilters({ ...filters, titleScope: v })} />
                    </div>
                    <div className="text-[11px] text-gray-400 flex items-center gap-1.5">
                      <TitleSyntaxTip />
                      Plain words · "exact phrase" · =exact title — click ⓘ for examples
                    </div>
                    <ChipInput label="" values={filters.titles}
                      excluded={filters.titlesExclude} onExcludedChange={(ex, inc) => setFilters({ ...filters, titlesExclude: ex, titles: inc })}
                      onChange={(v) => setFilters({ ...filters, titles: v })} placeholder={'e.g. hv engineer · "sales director" · =CTO'} />
                    <SuggestRelatedTitles titles={filters.titles} authHeaders={authHeaders}
                      context={[...filters.keywordGroups.flatMap((g) => g.terms), ...filters.locations].join(', ')}
                      onAdd={(v) => setFilters({ ...filters, titles: v })} />
                  </FilterSection>
                  <FilterSection title="Keywords" count={filters.keywordGroups.reduce((n, g) => n + g.terms.length + g.exclude.length, 0)} tour="search-filter-keywords" openSignal={keywordSignal}>
                    <KeywordGroupsEditor key={activePersona} groups={filters.keywordGroups} authHeaders={authHeaders} context={filters.titles.join(', ')}
                      onChange={(g) => setFilters({ ...filters, keywordGroups: g })} />
                  </FilterSection>
                  <FilterSection title="Skills" count={filters.skills.length + filters.skillsExclude.length}>
                    <ChipInput label="" values={filters.skills} splitCommas
                      excluded={filters.skillsExclude} onExcludedChange={(ex, inc) => setFilters({ ...filters, skillsExclude: ex, skills: inc })}
                      onChange={(v) => setFilters({ ...filters, skills: v })} placeholder="e.g. Kubernetes, Terraform (comma = OR)" />
                  </FilterSection>
                  <FilterSection title="Location" count={filters.locations.length + (filters.nearPlace.trim() ? 1 : 0)} openSignal={locationSignal}>
                    <LocationInput values={filters.locations} authHeaders={authHeaders}
                      onChange={(v) => setFilters({ ...filters, locations: v })} placeholder="Start typing a place — e.g. London" />
                    <NearInput place={filters.nearPlace} miles={filters.nearMiles} authHeaders={authHeaders}
                      onChange={(place, miles) => setFilters({ ...filters, nearPlace: place, nearMiles: miles })} />
                  </FilterSection>
                  <FilterSection title="Employment type" count={filters.employmentTypes.length}>
                    <div className="flex justify-end">
                      <ScopeSelect value={filters.employmentScope} onChange={(v) => setFilters({ ...filters, employmentScope: v })} />
                    </div>
                    <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                      {EMPLOYMENT_TYPE_GROUPS.map(({ label: t }) => (
                        <label key={t} className="flex items-center gap-1.5 text-xs text-gray-600">
                          <input
                            type="checkbox"
                            checked={filters.employmentTypes.indexOf(t) !== -1}
                            onChange={(e) => setFilters({
                              ...filters,
                              employmentTypes: e.target.checked
                                ? filters.employmentTypes.concat([t])
                                : filters.employmentTypes.filter((x) => x !== t),
                            })}
                          />
                          {t}
                        </label>
                      ))}
                    </div>
                    {signalsApply(filters.employmentTypes) && (
                      <EmploymentSignalsToggle checked={filters.employmentSignals}
                        onChange={(v) => setFilters({ ...filters, employmentSignals: v })} />
                    )}
                  </FilterSection>
                  <FilterSection title="Tenure &amp; experience"
                    count={(filters.tenureMin ? 1 : 0) + (filters.tenureMax ? 1 : 0) + (filters.joinedMin ? 1 : 0) + (filters.joinedMax ? 1 : 0) + (filters.experienceMin ? 1 : 0) + (filters.experienceMax ? 1 : 0)}>
                    <div>
                      <div className="text-xs font-semibold text-gray-600 mb-1">Time in current role (yrs)</div>
                      <div className="flex items-center gap-1">
                        <input type="number" min={0} max={50} placeholder="0" className="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm"
                          value={filters.tenureMin} onChange={(e) => setFilters({ ...filters, tenureMin: e.target.value })} />
                        <span className="text-gray-400">–</span>
                        <input type="number" min={0} max={50} placeholder="25+" className="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm"
                          value={filters.tenureMax} onChange={(e) => setFilters({ ...filters, tenureMax: e.target.value })} />
                      </div>
                      <p className="text-[11px] text-gray-400 mt-1">Restarts with a promotion or new title.</p>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-gray-600 mb-1">Time in current company (yrs)</div>
                      <div className="flex items-center gap-1">
                        <input type="number" min={0} max={50} step="0.5" placeholder="0" className="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm"
                          value={filters.joinedMin} onChange={(e) => setFilters({ ...filters, joinedMin: e.target.value })} />
                        <span className="text-gray-400">–</span>
                        <input type="number" min={0} max={50} step="0.5" placeholder="25+" className="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm"
                          value={filters.joinedMax} onChange={(e) => setFilters({ ...filters, joinedMax: e.target.value })} />
                      </div>
                      <p className="text-[11px] text-gray-400 mt-1">Since they joined the company: 0 – 1 = recent hires, promotions excluded.</p>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-gray-600 mb-1">Total experience (yrs)</div>
                      <div className="flex items-center gap-1">
                        <input type="number" min={0} max={60} placeholder="0" className="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm"
                          value={filters.experienceMin} onChange={(e) => setFilters({ ...filters, experienceMin: e.target.value })} />
                        <span className="text-gray-400">–</span>
                        <input type="number" min={0} max={60} placeholder="25+" className="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm"
                          value={filters.experienceMax} onChange={(e) => setFilters({ ...filters, experienceMax: e.target.value })} />
                      </div>
                    </div>
                  </FilterSection>
                  <FilterSection title="Education" count={filters.schools.length + filters.degrees.length}>
                    <SuggestChipInput kind="school" label="School" values={filters.schools} authHeaders={authHeaders}
                      onChange={(v) => setFilters({ ...filters, schools: v })} placeholder="Start typing — e.g. Newcastle University" />
                    <SuggestChipInput kind="degree" label="Degree / field of study" values={filters.degrees} authHeaders={authHeaders}
                      onChange={(v) => setFilters({ ...filters, degrees: v })} placeholder="Start typing — e.g. Computer Science" />
                  </FilterSection>
                  <LanguageCertSections f={filters} set={(patch) => setFilters({ ...filters, ...patch })} languageNames={languageNames} />
                  <FilterSection title="Personal industry" count={filters.industries.length}>
                    <p className="text-[11px] text-gray-400 -mt-1">The industry of the person's role — company industry lives on the Companies tab.</p>
                    <div className="flex justify-end">
                      <ScopeSelect value={filters.industryScope} onChange={(v) => setFilters({ ...filters, industryScope: v })} />
                    </div>
                    <ChipInput label="" values={filters.industries}
                      onChange={(v) => setFilters({ ...filters, industries: v })} placeholder="e.g. Telecommunications"
                      suggestions={industryOpts ? industryOpts.role.values : LIVE_SEARCH_INDUSTRIES}
                      suggestionLabels={industryOpts?.role.labels} suggestionsId="dataset-industries" />
                  </FilterSection>
                  <FilterSection title="Company" count={filters.companies.length}>
                    <div className="flex justify-end">
                      <ScopeSelect value={filters.companyScope} allowPast
                        onChange={(v) => setFilters({ ...filters, companyScope: v })} />
                    </div>
                    <SuggestChipInput kind="company" values={filters.companies} authHeaders={authHeaders}
                      onChange={(v) => setFilters({ ...filters, companies: v })} placeholder="Start typing a company, or paste a domain / LinkedIn URL" />
                    <button className="text-xs text-teal-700 hover:underline" onClick={() => { setBulkText(''); setBulkOpen('people'); }}>
                      + Add in bulk (paste or CSV, up to 25,000)
                    </button>
                  </FilterSection>
                  {/* Company-side filters live in the Companies tab and carry
                      over automatically — this just shows they're active. */}
                  {companyFilterCount > 0 && (
                    <div className="p-3">
                      <div className="flex items-start justify-between gap-2 bg-indigo-50 border border-indigo-200 rounded-lg px-2.5 py-2 text-xs text-indigo-800">
                        <span>
                          <span className="font-semibold">{companyFilterCount} company filter{companyFilterCount === 1 ? '' : 's'}</span> carried
                          over from the Companies tab{usingPersonas ? ` for ${personaMeta[activePersona]?.name || 'this persona'}` : ''} — people must currently work at a matching company.
                        </span>
                        <button className="text-indigo-500 hover:text-indigo-800 shrink-0" title="Clear all company filters"
                          onClick={() => { setCompanyFilters(EMPTY_COMPANY_FILTERS); setCompanyResults([]); setCompanyTotal(null); }}>✕</button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="p-3 border-t border-gray-100 space-y-2">
                  <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer" title="One row per company with matched counts — companies you listed show even at 0 matches">
                    <input type="checkbox" checked={groupMode} onChange={(e) => setGroupMode(e.target.checked)} />
                    Group results by company
                  </label>
                  {globalAsk && (
                    <GlobalSearchNotice
                      onAddLocation={() => { setGlobalAsk(false); setLocationSignal((n) => n + 1); }}
                      onSearchAnyway={() => { globalOkRef.current = true; setGlobalAsk(false); startPeopleSearch(); }}
                    />
                  )}
                  <button
                    data-tour="search-run"
                    className="w-full px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50"
                    disabled={searching}
                    onClick={() => {
                      // No location → ask first (once per visit), unless in a lesson.
                      if (source === 'universe' && !inLesson && !globalOkRef.current && !hasPeopleLocation(peoplePayload())) {
                        setGlobalAsk(true);
                        return;
                      }
                      startPeopleSearch();
                    }}
                  >
                    {searching ? 'Searching…' : 'Search'}
                  </button>
                </div>
              </div>
              )}

              {/* Results */}
              <div data-tour="search-results" className="flex-1 min-w-0 max-md:w-full max-md:scroll-mt-16">
                {drill && (
                  <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-indigo-50 border border-indigo-200 rounded-lg text-sm text-indigo-900">
                    <span>
                      {drill.keepFilters
                        ? <>Matches at <span className="font-semibold">{drill.name}</span> (your filters applied)</>
                        : <>All employees at <span className="font-semibold">{drill.name}</span> — other filters off, pick people individually</>}
                    </span>
                    <button className="ml-auto text-xs text-indigo-700 hover:underline whitespace-nowrap" onClick={exitDrill}>
                      ← Back to {groupMode ? 'grouped results' : 'search'}
                    </button>
                  </div>
                )}
                {loaderPhase === 'on' || loaderPhase === 'leaving' ? (
                  <SearchLoader leaving={loaderPhase === 'leaving'} />
                ) : groupRows !== null ? (
                  <>
                    <div className="text-sm text-gray-700 font-semibold tabular-nums mb-3">
                      {groupRows.length.toLocaleString()} compan{groupRows.length === 1 ? 'y' : 'ies'}
                      <span className="text-gray-400 font-normal"> · click a count to open that company's people — a 0 lists every employee so you can pick individually</span>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 overflow-auto" style={{ maxHeight: 'calc(100vh - 290px)' }}>
                      <table className="w-full text-[13px]">
                        <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
                          <tr className="text-left text-[11px] text-gray-500 uppercase tracking-wide">
                            <th className="px-3 py-2.5 min-w-[240px]">Company</th>
                            <th className="px-3 py-2.5 min-w-[150px]">Domain</th>
                            <th className="px-3 py-2.5 min-w-[110px] text-right">Matched</th>
                            <th className="px-3 py-2.5 min-w-[130px] text-right">Employees in data</th>
                            <th className="px-3 py-2.5 min-w-[70px]">LinkedIn</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupRows.length === 0 && (
                            <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">No companies matched</td></tr>
                          )}
                          {groupRows.map((g: any) => (
                            <tr key={g.key} className={`border-t border-gray-100 hover:bg-teal-50/60 ${g.requested ? 'bg-amber-50/40' : ''}`}>
                              <td className="px-3 py-2 font-medium text-gray-900">
                                {g.name}
                                {g.requested && <span className="ml-2 text-[9px] uppercase tracking-wide bg-amber-100 text-amber-700 rounded px-1 py-0.5">your list</span>}
                              </td>
                              <td className="px-3 py-2 text-gray-500">{g.domain || ''}</td>
                              <td className="px-3 py-2 text-right">
                                <button
                                  className={`tabular-nums font-semibold rounded px-2 py-0.5 ${g.matched > 0 ? 'text-teal-700 hover:bg-teal-100' : 'text-red-500 hover:bg-red-50'}`}
                                  title={g.matched > 0 ? 'Open the matching people at this company' : 'No matches here — click to list every employee and pick individually'}
                                  onClick={() => drillIntoCompany(g, g.matched > 0)}
                                >
                                  {g.matched}
                                </button>
                              </td>
                              <td className="px-3 py-2 text-right">
                                <button
                                  className="tabular-nums text-gray-600 hover:bg-gray-100 rounded px-2 py-0.5"
                                  title="List every employee of this company in the data"
                                  onClick={() => drillIntoCompany(g, false)}
                                  disabled={!g.employees}
                                >
                                  {g.employees}
                                </button>
                              </td>
                              <td className="px-3 py-2">
                                {g.linkedin_url && (
                                  <a href={g.linkedin_url.startsWith('http') ? g.linkedin_url : `https://${g.linkedin_url}`} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-800 text-xs">Open ↗</a>
                                )}
                                {/* Admins: hide a pseudo-employer ("Self-employed"…) everywhere (migration 232) */}
                                {(user as any)?.role === 'admin' && (
                                  <button
                                    className="block text-[11px] text-gray-400 hover:text-red-600 mt-0.5"
                                    title="Hide this company everywhere — Search, suggestions and imports, for every account. Its people stay. Undo in Admin → Hidden companies."
                                    onClick={async () => {
                                      if (!window.confirm(`Hide "${g.name}" everywhere? It won't show as a company in Search, suggestions or imports for any account. Its people stay. You can undo this in Admin → Hidden companies.`)) return;
                                      const entries = [g.name, g.linkedin_url].filter(Boolean);
                                      const r = await fetch('/api/admin/hidden-companies', { method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ entries }) });
                                      if (r.ok) setGroupRows((rows) => (rows || []).filter((x: any) => x.key !== g.key));
                                      else alert((await r.json().catch(() => ({}))).error || 'Could not hide it');
                                    }}
                                  >Hide</button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : searchTotal === null ? (
                  <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-gray-400 text-sm">
                    Build a search on the left, then hit Search. Matches can be poured straight into a
                    MarketMapr project.
                  </div>
                ) : (
                  <>
                    {searchTotal === 0 && loadedFrom && projectCtx?.onlyNew && !!projectCtx.matchesAlreadyIn && (
                      <div className="mb-3 rounded-lg border border-teal-200 bg-teal-50 px-3 py-2 text-sm text-teal-900">
                        No one new: all {projectCtx.matchesAlreadyIn.toLocaleString()} {projectCtx.matchesAlreadyIn === 1 ? 'person' : 'people'} who match are already in {projectCtx.projectName}.{' '}
                        <button
                          className="underline font-medium hover:text-teal-950"
                          onClick={() => { onlyNewRef.current = false; setOnlyNewToProject(false); runSearch(0); }}
                        >Show them</button>
                        {' '}or widen the search to find more.
                      </div>
                    )}
                    <div className="flex items-center gap-3 mb-3 flex-wrap">
                      <div className="text-sm text-gray-700 font-semibold tabular-nums">
                        {searchTotal.toLocaleString()} match{searchTotal === 1 ? '' : 'es'}
                      </div>
                      {groupCounts && groupCounts.length > 1 && groupCounts.map((n, i) => {
                        const c = PERSONA_COLORS[i % PERSONA_COLORS.length];
                        return (
                          <span key={i} className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
                            <span className={`w-2 h-2 rounded-full ${c.solid}`} />
                            {activePersonaPairs()[i]?.name || `Persona ${i + 1}`}: {n.toLocaleString()}
                          </span>
                        );
                      })}
                      {searchTotal > 0 && (
                        <div data-tour="search-add-project" className="ml-auto flex items-center gap-2 flex-wrap justify-end">
                          {projectTargetPicker('w-56')}
                          {(() => {
                            const lim = source === 'universe' && !onTrial ? universeStatus?.importLimit : null;
                            const over = !!lim && searchTotal > lim;
                            const trialCut = onTrial && source === 'universe' && searchTotal > trialLimit;
                            return (
                              <>
                                <button
                                  className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50"
                                  disabled={addingToProject || !addTargetReady || over}
                                  title={over ? `Your ${universeStatus?.importLimitPlan} plan imports up to ${lim!.toLocaleString()} people per project — narrow the search to import` : addTargetName ? `Add to “${addTargetName}” — people already in it are skipped` : undefined}
                                  onClick={addResultsToProject}
                                >
                                  {addingToProject ? (addTarget ? 'Adding…' : 'Creating…') : trialCut ? `Add first ${trialLimit.toLocaleString()} to project →` : `Add ${searchTotal.toLocaleString()} to project →`}
                                </button>
                                {trialCut && (
                                  <span className="text-[11px] text-teal-800 basis-full text-right">
                                    Free trial: imports add the first {trialLimit.toLocaleString()} of these {searchTotal.toLocaleString()}.{' '}
                                    <a href="/settings?tab=subscription" className="underline font-medium">Activate your plan</a> to add them all.
                                  </span>
                                )}
                                {over && (
                                  <span className="text-[11px] text-amber-700 basis-full text-right">
                                    Over your {universeStatus?.importLimitPlan} plan's {lim!.toLocaleString()}-per-project import limit — narrow the search
                                    {universeStatus?.importTierKey === 'scale'
                                      ? <>, or reach out to your MarketMapr account manager for higher limits.</>
                                      : <>, or <a href="/settings?tab=subscription" className="underline font-medium">upgrade your plan</a> to add more.</>}
                                  </span>
                                )}
                                {!over && !onTrial && universeStatus?.monthlyImports && (() => {
                                  const m = universeStatus.monthlyImports!;
                                  const short = searchTotal > m.left;
                                  if (!short && m.used < m.limit * 0.75) return null;
                                  return (
                                    <span className={`text-[11px] basis-full text-right ${short ? 'text-amber-700' : 'text-gray-500'}`}>
                                      {m.left.toLocaleString()} of your {m.limit.toLocaleString()} monthly imports left
                                      {short && (universeStatus?.importTierKey === 'scale'
                                        ? <> — not enough for these; reach out to your MarketMapr account manager for higher limits.</>
                                        : <> — not enough for these. <a href="/settings?tab=subscription" className="underline font-medium">Upgrade your plan</a> for more.</>)}
                                    </span>
                                  );
                                })()}
                              </>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                    <div className={`bg-white rounded-xl border border-gray-200 overflow-auto ${searching ? 'opacity-50 pointer-events-none' : ''}`} style={{ maxHeight: 'calc(100vh - 260px)' }}>
                      <table className="text-[13px]" style={{ minWidth: '1650px', width: '100%' }}>
                        <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
                          <tr className="text-left text-[11px] text-gray-500 uppercase tracking-wide">
                            <th className="px-3 py-2.5 sticky left-0 bg-gray-50 z-20 min-w-[170px]">Name</th>
                            <th className="px-3 py-2.5 min-w-[200px]">Title</th>
                            <th className="px-3 py-2.5 min-w-[170px]">Company</th>
                            <th className="px-3 py-2.5 min-w-[180px]">Location</th>
                            <th className="px-3 py-2.5 min-w-[110px]">Employment</th>
                            <th className="px-3 py-2.5 min-w-[70px] text-right">In role</th>
                            <th className="px-3 py-2.5 min-w-[70px] text-right">Exp</th>
                            <th className="px-3 py-2.5 min-w-[260px]">Headline</th>
                            <th className="px-3 py-2.5 min-w-[180px]">Email</th>
                            <th className="px-3 py-2.5 min-w-[120px]">Phone</th>
                            <th className="px-3 py-2.5 min-w-[260px]">Skills</th>
                            <th className="px-3 py-2.5 min-w-[70px]">LinkedIn</th>
                            <th className="px-3 py-2.5 min-w-[40px]" title="Geocoded">📍</th>
                          </tr>
                        </thead>
                        <tbody>
                          {searchResults.length === 0 && (
                            <tr><td colSpan={13} className="px-4 py-8 text-center text-gray-400">No matches</td></tr>
                          )}
                          {searchResults.map((p) => (
                            <tr key={p.id} className="border-t border-gray-100 hover:bg-teal-50/60 cursor-pointer group" onClick={() => openDetail(p.id)}>
                              <td className="px-3 py-2 font-medium text-gray-900 sticky left-0 bg-white group-hover:bg-teal-50 whitespace-nowrap">
                                {p.name || <span className="text-gray-300">—</span>}
                                {(p as any).inProject && (
                                  <span className="ml-1.5 inline-block align-middle text-[10px] font-semibold bg-teal-100 text-teal-800 rounded px-1.5 py-0.5"
                                    title={`Already in ${projectCtx ? projectCtx.projectName : 'this project'}`}>✓ In this project</span>
                                )}
                                {Array.isArray((p as any).matched_groups) && groupCounts && groupCounts.length > 1 && (
                                  <span className="ml-1.5 inline-flex items-center gap-1 align-middle">
                                    {(p as any).matched_groups.map((gi: number) => (
                                      <span key={gi} className={`w-2 h-2 rounded-full ${PERSONA_COLORS[gi % PERSONA_COLORS.length].solid}`}
                                        title={activePersonaPairs()[gi]?.name || `Persona ${gi + 1}`} />
                                    ))}
                                  </span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-gray-600 max-w-[220px]">
                                <span className="truncate block">{p.title || ''}</span>
                                {(p as any).matched_current_role && (
                                  <span className="block text-[11px] text-teal-700 truncate" title="This person also holds this current role — it's what matched your title filter">
                                    ↳ {(p as any).matched_current_role} · concurrent role
                                  </span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-gray-600 max-w-[190px] truncate">{p.company || ''}</td>
                              <td className="px-3 py-2 text-gray-500 max-w-[200px] truncate">{p.location || ''}</td>
                              <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{p.employment_type || ''}</td>
                              <td className="px-3 py-2 text-gray-500 text-right tabular-nums">{yearsSince(p.current_start)}</td>
                              <td className="px-3 py-2 text-gray-500 text-right tabular-nums">
                                {p.experience_years !== null && p.experience_years !== undefined ? Number(p.experience_years).toFixed(1) : ''}
                              </td>
                              <td className="px-3 py-2 text-gray-500 max-w-[280px] truncate">{p.headline || ''}</td>
                              <td className="px-3 py-2 text-gray-600 max-w-[200px] truncate">{p.email || ''}</td>
                              <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{p.phone || ''}</td>
                              <td className="px-3 py-2 text-gray-500 max-w-[280px] truncate">{p.skills || ''}</td>
                              <td className="px-3 py-2">
                                {p.linkedin_url && (
                                  <a
                                    href={p.linkedin_url.startsWith('http') ? p.linkedin_url : `https://${p.linkedin_url}`}
                                    target="_blank" rel="noopener noreferrer"
                                    className="text-teal-600 hover:text-teal-800 text-xs whitespace-nowrap"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    Open ↗
                                  </a>
                                )}
                              </td>
                              <td className="px-3 py-2 text-center">{p.geocoded ? '🟢' : <span className="text-gray-200">·</span>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {searchTotal > PAGE_SIZE && (
                      <div className="flex items-center justify-between mt-3 text-sm">
                        <button className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
                          disabled={searchPage === 0 || searching} onClick={() => runSearch(searchPage - 1)}>← Prev</button>
                        <span className="text-gray-500 tabular-nums">Page {searchPage + 1} of {Math.max(1, Math.ceil(Math.min(searchTotal, 1000) / PAGE_SIZE))}</span>
                        <button className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
                          disabled={(searchPage + 1) * PAGE_SIZE >= Math.min(searchTotal, 1000) || searching} onClick={() => runSearch(searchPage + 1)}>Next →</button>
                        {searchTotal > 1000 && (
                          <span className="text-[11px] text-gray-400 ml-2" title="Totals always show, but browsing stops at the first 1,000 — narrow the search, or add the full set to a project to work with all of them">
                            Browsing the first 1,000 of {searchTotal.toLocaleString()} — refine, or add all to a project
                          </span>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
              </div>
              ) : (
              /* ---- Companies search mode ---- */
              <div className="flex gap-4 items-start max-md:flex-col max-md:items-stretch">
                <div className="w-72 flex-shrink-0 bg-white rounded-xl border border-gray-200 flex flex-col max-h-[calc(100vh-180px)] max-md:w-full max-md:max-h-none">
                  <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-100">
                    <span className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                      Company filters
                      {companyFilterCount > 0 && (
                        <span className="text-[10px] bg-teal-600 text-white rounded-full px-1.5 py-0.5 font-semibold tabular-nums">{companyFilterCount}</span>
                      )}
                    </span>
                    <div className="flex items-center gap-1">
                      {source === 'universe' && !personasEnabled && (
                        <button className="text-xs text-teal-700 hover:text-teal-900 px-1"
                          title="Combine several company searches — e.g. CDMO in the description OR in specialties. The same personas apply on the People tab."
                          onClick={addPersona}>
                          + Personas
                        </button>
                      )}
                      {companyFilterCount > 0 && (
                        <button className="text-xs text-gray-400 hover:text-gray-700 px-1" onClick={() => { setCompanyFilters(EMPTY_COMPANY_FILTERS); setCompanyResults([]); setCompanyTotal(null); }}>
                          Clear all
                        </button>
                      )}
                    </div>
                  </div>
                  {personaBar}
                  {source !== 'universe' && selected.kind !== 'companies' && (
                    <div className="px-3 py-2.5 border-b border-gray-100 space-y-2">
                      {(() => {
                        const linkedDs = datasets.filter((d) => d.id === selected.linked_dataset_id)[0];
                        const options = datasets.filter((d) => d.kind === 'companies' && d.status === 'ready');
                        return (
                          <>
                            {linkedDs ? (
                              <div className="text-[11px] text-gray-500">
                                Searching <span className="font-semibold text-gray-700">{linkedDs.name}</span>
                                {linkInfo && <span> · {Number(linkInfo.current_matched).toLocaleString()}/{Number(linkInfo.current_total).toLocaleString()} current roles matched</span>}
                                {' '}· filters here carry over to People automatically.
                              </div>
                            ) : (
                              <div className="text-[11px] text-amber-700">Link a companies dataset to search companies and unlock company filters on People.</div>
                            )}
                            {options.length > 0 && (
                              <div className="flex gap-1.5">
                                <select className="flex-1 border border-gray-300 rounded-lg px-2 py-1 text-xs bg-white focus:outline-none"
                                  value={linkChoice} onChange={(e) => setLinkChoice(e.target.value)}>
                                  <option value="">Choose companies dataset…</option>
                                  {options.map((o) => <option key={o.id} value={String(o.id)}>{o.name}</option>)}
                                </select>
                                <button className="px-2.5 py-1 bg-teal-600 text-white rounded-lg text-xs font-medium hover:bg-teal-700 disabled:opacity-50"
                                  disabled={!linkChoice || linking} onClick={handleLink}>
                                  {linking ? 'Linking…' : linkedDs ? 'Re-link' : 'Link'}
                                </button>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}
                  <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
                    <FilterSection title="Company name keywords" count={companyFilters.nameKeywords.length} defaultOpen>
                      <ChipInput label="" values={companyFilters.nameKeywords}
                        onChange={(v) => setCompanyFilters({ ...companyFilters, nameKeywords: v })} placeholder="e.g. consulting" />
                    </FilterSection>
                    <FilterSection title="Industry" count={companyFilters.industries.length}>
                      <ChipInput label="" values={companyFilters.industries}
                        onChange={(v) => setCompanyFilters({ ...companyFilters, industries: v })} placeholder="e.g. Financial Services"
                        suggestions={industryOpts ? industryOpts.company.values : LIVE_SEARCH_INDUSTRIES}
                        suggestionLabels={industryOpts?.company.labels} suggestionsId="company-mode-industries" />
                    </FilterSection>
                    <FilterSection title="Location" count={companyFilters.locations.length}>
                      <CompanyLocationInput values={companyFilters.locations} authHeaders={authHeaders}
                        onChange={(v) => setCompanyFilters({ ...companyFilters, locations: v })} />
                    </FilterSection>
                    <FilterSection title="Headcount" count={(companyFilters.headcountMin ? 1 : 0) + (companyFilters.headcountMax ? 1 : 0)}>
                      <div className="flex items-center gap-1">
                        <input type="number" min={0} placeholder="Min" className="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm"
                          value={companyFilters.headcountMin} onChange={(e) => setCompanyFilters({ ...companyFilters, headcountMin: e.target.value })} />
                        <span className="text-gray-400">–</span>
                        <input type="number" min={0} placeholder="Max" className="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm"
                          value={companyFilters.headcountMax} onChange={(e) => setCompanyFilters({ ...companyFilters, headcountMax: e.target.value })} />
                      </div>
                    </FilterSection>
                    <FilterSection title="Founded" count={(companyFilters.foundedAfter ? 1 : 0) + (companyFilters.foundedBefore ? 1 : 0)}>
                      <div className="flex items-center gap-1">
                        <input type="number" min={1800} max={2100} placeholder="After (year)" className="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm"
                          value={companyFilters.foundedAfter} onChange={(e) => setCompanyFilters({ ...companyFilters, foundedAfter: e.target.value })} />
                        <span className="text-gray-400">–</span>
                        <input type="number" min={1800} max={2100} placeholder="Before" className="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm"
                          value={companyFilters.foundedBefore} onChange={(e) => setCompanyFilters({ ...companyFilters, foundedBefore: e.target.value })} />
                      </div>
                    </FilterSection>
                    <FilterSection title="Description keywords" count={companyFilters.descriptionKeywords.length}>
                      <ChipInput label="" values={companyFilters.descriptionKeywords}
                        onChange={(v) => setCompanyFilters({ ...companyFilters, descriptionKeywords: v })} />
                    </FilterSection>
                    <FilterSection title="Specialties / keywords" count={companyFilters.keywords.length}>
                      <ChipInput label="" values={companyFilters.keywords} splitCommas
                        onChange={(v) => setCompanyFilters({ ...companyFilters, keywords: v })} placeholder="e.g. wealth management, pensions (comma = OR)" />
                    </FilterSection>
                    <FilterSection title="Technologies" count={companyFilters.technologies.length}>
                      <ChipInput label="" values={companyFilters.technologies}
                        onChange={(v) => setCompanyFilters({ ...companyFilters, technologies: v })} placeholder="e.g. AWS, Shopify" />
                    </FilterSection>
                    <FilterSection title="Specific companies" count={companyFilters.companiesList.length}>
                      <SuggestChipInput kind="company" values={companyFilters.companiesList} authHeaders={authHeaders}
                        onChange={(v) => setCompanyFilters({ ...companyFilters, companiesList: v })} placeholder="Start typing a company, or paste a domain / LinkedIn URL" />
                      <button className="text-xs text-teal-700 hover:underline" onClick={() => { setBulkText(''); setBulkOpen('companies'); }}>
                        + Add in bulk (paste or CSV)
                      </button>
                    </FilterSection>
                  </div>
                  <div className="p-3 border-t border-gray-100">
                    <button
                      className="w-full px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50"
                      disabled={searching || (source !== 'universe' && !companyTargetId)}
                      onClick={() => runCompanySearch(0)}
                    >
                      {searching ? 'Searching…' : 'Search companies'}
                    </button>
                  </div>
                </div>

                <div data-mobile-results="companies" className="flex-1 min-w-0 max-md:w-full max-md:scroll-mt-16">
                  {companyTotal === null ? (
                    <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-gray-400 text-sm">
                      Filter the company universe — industry, headcount, founded year, technologies —
                      then search people across the matches in one click.
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-3 mb-3 flex-wrap">
                        <div className="text-sm text-gray-700 font-semibold tabular-nums">
                          {companyTotal.toLocaleString()} compan{companyTotal === 1 ? 'y' : 'ies'}
                        </div>
                        {companyGroupCounts && companyGroupCounts.length > 1 && companyGroupCounts.map((n, i) => {
                          const pairs = activeCompanyPersonaPairs();
                          const idx = personaMeta.findIndex((m) => m.name === pairs[i]?.name);
                          const c = PERSONA_COLORS[(idx >= 0 ? idx : i) % PERSONA_COLORS.length];
                          return (
                            <span key={i} className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
                              <span className={`w-2 h-2 rounded-full ${c.solid}`} />
                              {pairs[i]?.name || `Persona ${i + 1}`}: {n.toLocaleString()}
                            </span>
                          );
                        })}
                        {/* Two distinct imports — companies stand alone as a
                            research set, or come with their candidates. */}
                        {companyTotal > 0 && (
                          <div className="ml-auto flex items-center gap-2 flex-wrap">
                            {projectTargetPicker('w-48')}
                            <button
                              className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
                              disabled={addingToProject || !addTargetReady}
                              title={addTargetName ? `Add just these companies to “${addTargetName}” — ones already in it are skipped` : 'Create a project holding just these companies — research them first, source people later'}
                              onClick={addCompaniesOnlyToProject}
                            >
                              {onTrial && companyTotal > trialLimit ? `Add first ${trialLimit.toLocaleString()} companies only →` : `Add ${companyTotal.toLocaleString()} companies only →`}
                            </button>
                            {(source === 'universe' || selected.kind !== 'companies') && (
                              <button
                                className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50"
                                disabled={addingToProject || !addTargetReady}
                                title="Import the candidates matching your People filters at these companies — their employers arrive automatically"
                                onClick={addResultsToProject}
                              >
                                Add candidates + companies →
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="bg-white rounded-xl border border-gray-200 overflow-auto" style={{ maxHeight: 'calc(100vh - 290px)' }}>
                        <table className="w-full text-[13px]">
                          <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
                            <tr className="text-left text-[11px] text-gray-500 uppercase tracking-wide">
                              <th className="px-3 py-2.5 min-w-[200px]">Company</th>
                              <th className="px-3 py-2.5 min-w-[160px]">Industry</th>
                              <th className="px-3 py-2.5 min-w-[90px] text-right">Headcount</th>
                              <th className="px-3 py-2.5 min-w-[70px] text-right">Founded</th>
                              <th className="px-3 py-2.5 min-w-[180px]">Location</th>
                              <th className="px-3 py-2.5 min-w-[150px]">Domain</th>
                              <th className="px-3 py-2.5 min-w-[70px]">LinkedIn</th>
                              <th className="px-3 py-2.5 min-w-[220px]">Specialties</th>
                            </tr>
                          </thead>
                          <tbody>
                            {companyResults.length === 0 && (
                              <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">No matches</td></tr>
                            )}
                            {companyResults.map((c) => (
                              <tr key={c.id} className="border-t border-gray-100 hover:bg-teal-50/60">
                                <td className="px-3 py-2 font-medium text-gray-900">{c.name}</td>
                                <td className="px-3 py-2 text-gray-600">{c.industry || ''}</td>
                                <td className="px-3 py-2 text-gray-600 text-right tabular-nums">{c.headcount != null ? Number(c.headcount).toLocaleString() : (c.headcount_text || '')}</td>
                                <td className="px-3 py-2 text-gray-500 text-right tabular-nums">{c.founded_year || ''}</td>
                                <td className="px-3 py-2 text-gray-500 max-w-[220px] truncate">{c.location || ''}</td>
                                <td className="px-3 py-2">
                                  {c.domain && <a href={`https://${c.domain}`} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-800 text-xs">{c.domain}</a>}
                                </td>
                                <td className="px-3 py-2">
                                  {c.linkedin_url && (
                                    <a href={c.linkedin_url.startsWith('http') ? c.linkedin_url : `https://${c.linkedin_url}`} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-800 text-xs">Open ↗</a>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-gray-500 max-w-[260px] truncate">{c.specialties || ''}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {companyTotal > PAGE_SIZE && (
                        <div className="flex items-center justify-between mt-3 text-sm">
                          <button className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
                            disabled={companyPage === 0 || searching} onClick={() => runCompanySearch(companyPage - 1)}>← Prev</button>
                          <span className="text-gray-500 tabular-nums">Page {companyPage + 1} of {Math.max(1, Math.ceil(companyTotal / PAGE_SIZE))}</span>
                          <button className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
                            disabled={(companyPage + 1) * PAGE_SIZE >= companyTotal || searching} onClick={() => runCompanySearch(companyPage + 1)}>Next →</button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
              )}

              {/* Import-to-project progress modal */}
              {promoteJob && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
                  <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5">
                    <p className="text-sm font-semibold text-gray-900">
                      {promoteJob.error ? 'Import problem' : promoteJob.done ? 'Done — opening project…' : `Adding to “${promoteJob.projectName}”`}
                    </p>
                    {promoteJob.error ? (
                      <>
                        <p className="text-sm text-red-600 mt-3">{promoteJob.error}</p>
                        <div className="flex justify-end mt-4">
                          <button className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200"
                            onClick={() => { setPromoteJob(null); setAddingToProject(false); }}>Close</button>
                        </div>
                      </>
                    ) : (
                      <div className="mt-4 space-y-4">
                        {promoteJob.total > 0 && (
                        <div>
                          <div className="flex justify-between text-xs text-gray-500 mb-1 tabular-nums">
                            <span>Candidates</span>
                            <span>{(promoteJob.inserted || 0).toLocaleString()} / {(promoteJob.total || 0).toLocaleString()}</span>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-teal-500 rounded-full transition-all duration-500"
                              style={{ width: `${promoteJob.total ? Math.min(100, Math.round(100 * (promoteJob.inserted || 0) / promoteJob.total)) : 0}%` }} />
                          </div>
                        </div>
                        )}
                        <div className={promoteJob.phase === 'candidates' ? 'opacity-40' : ''}>
                          <div className="flex justify-between text-xs text-gray-500 mb-1 tabular-nums">
                            <span>Companies (current employers)</span>
                            <span>{promoteJob.companiesTotal != null ? `${(promoteJob.companiesDone || 0).toLocaleString()} / ${promoteJob.companiesTotal.toLocaleString()}` : '—'}</span>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-indigo-400 rounded-full transition-all duration-500"
                              style={{ width: `${promoteJob.companiesTotal ? Math.min(100, Math.round(100 * (promoteJob.companiesDone || 0) / promoteJob.companiesTotal)) : 0}%` }} />
                          </div>
                        </div>
                        <p className="text-[11px] text-gray-400">
                          Geocoding continues in the background after the project opens. Keep this tab open until it finishes.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Bulk company entry modal (paste or CSV upload) */}
              {bulkOpen && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setBulkOpen(null)}>
                  <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col" onClick={(e) => e.stopPropagation()}>
                    <div className="px-4 py-3 border-b">
                      <p className="text-sm font-semibold text-gray-900">Add companies in bulk</p>
                      <p className="text-xs text-gray-500 mt-0.5">Paste LinkedIn company URLs, domains or names (one per line), or upload a CSV — URLs and domains are picked out of any column. Up to 25,000. Websites/domains or LinkedIn URLs match most reliably; for lists over 500, plain names must match the company name exactly.</p>
                    </div>
                    <div className="p-4 space-y-3">
                      <textarea
                        className="w-full h-44 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal-500"
                        placeholder={'linkedin.com/company/acme\nacme.com\nAcme Consulting'}
                        value={bulkText}
                        onChange={(e) => setBulkText(e.target.value)}
                      />
                      <div className="flex items-center gap-3">
                        <label className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-600 cursor-pointer hover:bg-gray-50">
                          Upload CSV…
                          <input type="file" accept=".csv,.txt,.tsv" className="hidden"
                            onChange={(e) => {
                              const file = e.target.files && e.target.files[0];
                              if (!file) return;
                              const reader = new FileReader();
                              reader.onload = () => setBulkText((prev) => (prev ? prev + '\n' : '') + String(reader.result || ''));
                              reader.readAsText(file);
                              e.target.value = '';
                            }} />
                        </label>
                        <span className="text-xs text-gray-500 tabular-nums">
                          {parseCompanyEntries(bulkText).length} companies detected
                        </span>
                      </div>
                    </div>
                    <div className="px-4 py-3 border-t flex justify-end gap-2">
                      <button className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg" onClick={() => setBulkOpen(null)}>Cancel</button>
                      <button
                        className="px-4 py-1.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50"
                        disabled={!parseCompanyEntries(bulkText).length}
                        onClick={() => {
                          const entries = parseCompanyEntries(bulkText);
                          if (bulkOpen === 'people') {
                            setFilters({ ...filters, companies: Array.from(new Set(filters.companies.concat(entries))).slice(0, 25000), companyScope: 'current' });
                          } else {
                            setCompanyFilters({ ...companyFilters, companiesList: Array.from(new Set(companyFilters.companiesList.concat(entries))).slice(0, 25000) });
                          }
                          setBulkOpen(null);
                          setBulkText('');
                        }}
                      >
                        Add {parseCompanyEntries(bulkText).length} companies
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ---- Detail drawer — the FULL record, not just promoted columns ---- */}
        {detail && (() => {
          const sd: any = (detail.raw && (detail.raw.serialized_data || detail.raw)) || {};
          const ym = (s: any) => (s ? String(s).slice(0, 7) : null);
          const range = (a: any, b: any) => {
            const s = ym(a), e = ym(b);
            return s || e ? `${s || '?'} – ${e || 'Present'}` : null;
          };
          const bySt = (k: string) => (x: any, y: any) => String(y?.[k] || '').localeCompare(String(x?.[k] || ''));
          const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
            <div className="mt-5">
              <h3 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">{title}</h3>
              {children}
            </div>
          );
          const Chips: React.FC<{ items: any[] }> = ({ items }) => (
            <div className="flex flex-wrap gap-1">
              {items.map((s, i) => <span key={i} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[11px]">{String(s)}</span>)}
            </div>
          );
          const roles = (Array.isArray(sd.roles) ? [...sd.roles] : []).sort(bySt('start_date'));
          const edus = (Array.isArray(sd.educations) ? [...sd.educations] : []).sort(bySt('start_date'));
          const skills: any[] = Array.isArray(sd.skills) ? sd.skills
            : detail.skills ? String(detail.skills).split(',').map((s: string) => s.trim()).filter(Boolean) : [];
          const langs = Array.isArray(sd.languages) ? sd.languages : [];
          const certs = Array.isArray(sd.certifications) ? sd.certifications : [];
          const techs = Array.isArray(sd.technologies) ? sd.technologies : [];
          const keywords: any[] = Array.isArray(sd.keywords) ? sd.keywords
            : detail.specialties ? String(detail.specialties).split(',').map((s: string) => s.trim()).filter(Boolean) : [];
          const addresses = Array.isArray(sd.addresses) ? sd.addresses : [];
          const funding = Array.isArray(sd.funding_rounds) ? sd.funding_rounds : [];
          // Vendor-hosted media URLs would name the provider in the network tab.
          const rawPhoto = sd.photo || sd.logo_url;
          const photo = rawPhoto && !new RegExp('fora' + 'ger', 'i').test(String(rawPhoto)) ? rawPhoto : null;
          const summary = sd.description || (!detail.__company ? detail.summary : null);
          const facts: [string, any][] = detail.__company
            ? [
                ['Industry', detail.industry || sd.industry],
                ['Headcount', sd.employees_number ? Number(sd.employees_number).toLocaleString() + (sd.employees_range ? ` (${sd.employees_range})` : '') : (detail.headcount_text || detail.headcount)],
                ['Founded', sd.founded_date ? String(sd.founded_date).slice(0, 4) : null],
                ['Location', detail.location || sd.location],
                ['Updated', sd.date_updated ? String(sd.date_updated).slice(0, 10) : null],
              ]
            : [
                ['Title', detail.title], ['Company', detail.company],
                ['Location', detail.location || sd.location],
                ['Email', detail.email], ['Phone', detail.phone],
                ['Industry', sd.industry],
                ['Connections', sd.number_of_connections != null ? `${Number(sd.number_of_connections).toLocaleString()} (${Number(sd.number_of_followers || 0).toLocaleString()} followers)` : null],
                ['Updated', sd.date_updated ? String(sd.date_updated).slice(0, 10) : null],
              ];
          return (
          <div className="w-[440px] border-l border-gray-200 bg-white overflow-y-auto flex-shrink-0">
            <div className="p-5">
              <div className="flex items-start justify-between gap-3">
                {photo && (
                  <img src={photo} alt="" className={`w-12 h-12 object-cover flex-shrink-0 ${detail.__company ? 'rounded-lg' : 'rounded-full'}`}
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-lg font-semibold text-gray-900">{detail.name || 'Record'}</div>
                  {detail.headline && <div className="text-sm text-gray-500 mt-0.5">{detail.headline}</div>}
                </div>
                <button className="text-gray-400 hover:text-gray-700" onClick={() => setDetail(null)}>✕</button>
              </div>

              {detailLoading ? (
                <div className="text-gray-400 text-sm mt-6">Loading…</div>
              ) : (
                <>
                  <div className="mt-4 space-y-1.5 text-sm">
                    {facts.map(([label, value]) => value ? (
                      <div key={label} className="flex">
                        <span className="w-24 text-gray-400 flex-shrink-0">{label}</span>
                        <span className="text-gray-800 break-words">{value}</span>
                      </div>
                    ) : null)}
                    {(detail.domain || detail.website) && (
                      <div className="flex">
                        <span className="w-24 text-gray-400 flex-shrink-0">Website</span>
                        <a href={detail.website || `https://${detail.domain}`} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline break-all">
                          {detail.domain || detail.website}
                        </a>
                      </div>
                    )}
                    {detail.linkedin_url && (
                      <div className="flex">
                        <span className="w-24 text-gray-400 flex-shrink-0">LinkedIn</span>
                        <a href={detail.linkedin_url.startsWith('http') ? detail.linkedin_url : `https://${detail.linkedin_url}`}
                          target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline break-all">
                          {detail.linkedin_url.replace(/^https?:\/\/(www\.)?/, '')}
                        </a>
                      </div>
                    )}
                  </div>

                  {summary && (
                    <Section title={detail.__company ? 'About' : 'Summary'}>
                      <p className="text-[13px] text-gray-700 whitespace-pre-wrap">{String(summary).slice(0, 1500)}{String(summary).length > 1500 ? '…' : ''}</p>
                    </Section>
                  )}

                  {roles.length > 0 && (
                    <Section title={`Career history (${roles.length})`}>
                      <div className="space-y-2.5">
                        {roles.map((r: any, i: number) => (
                          <div key={i} className="flex gap-2">
                            <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${!r.end_date ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                            <div className="min-w-0">
                              <div className="text-[13px]">
                                <span className="font-medium text-gray-900">{r.role_title || '—'}</span>
                                {r.organization_name && <span className="text-gray-500"> · {r.organization_name}</span>}
                              </div>
                              <div className="text-[11px] text-gray-400">
                                {range(r.start_date, r.end_date)}
                                {r.location ? ` · ${r.location}` : ''}
                                {r.organization_industry ? ` · ${r.organization_industry}` : ''}
                              </div>
                              {r.description && <p className="text-[11px] text-gray-500 mt-0.5">{String(r.description).slice(0, 220)}{String(r.description).length > 220 ? '…' : ''}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </Section>
                  )}

                  {edus.length > 0 && (
                    <Section title="Education">
                      <div className="space-y-1.5">
                        {edus.map((e: any, i: number) => (
                          <div key={i} className="text-[13px]">
                            <span className="font-medium text-gray-800">{e.school_name || '—'}</span>
                            <span className="text-gray-500">{e.degree ? ` · ${e.degree}` : ''}{e.field_of_study ? `, ${e.field_of_study}` : ''}{e.grade ? ` · ${e.grade}` : ''}</span>
                            {range(e.start_date, e.end_date) && <span className="text-[11px] text-gray-400"> · {range(e.start_date, e.end_date)}</span>}
                          </div>
                        ))}
                      </div>
                    </Section>
                  )}

                  {skills.length > 0 && <Section title="Skills"><Chips items={skills.slice(0, 30)} /></Section>}
                  {langs.length > 0 && (
                    <Section title="Languages">
                      <Chips items={langs.map((l: any) => l?.name ? `${l.name}${l.proficiency ? ` (${String(l.proficiency).toLowerCase().replace(/_/g, ' ')})` : ''}` : l)} />
                    </Section>
                  )}
                  {certs.length > 0 && (
                    <Section title={`Certifications (${certs.length})`}>
                      <div className="space-y-1">
                        {certs.slice(0, 12).map((c: any, i: number) => (
                          <div key={i} className="text-[12px] text-gray-700">{c.certificate_name}{c.authority ? <span className="text-gray-400"> · {c.authority}</span> : null}</div>
                        ))}
                      </div>
                    </Section>
                  )}

                  {keywords.length > 0 && <Section title="Specialties / keywords"><Chips items={keywords.slice(0, 25)} /></Section>}
                  {techs.length > 0 && <Section title="Technologies"><Chips items={techs.slice(0, 30)} /></Section>}
                  {addresses.length > 0 && (
                    <Section title="Offices">
                      <div className="space-y-1">
                        {addresses.slice(0, 8).map((a: any, i: number) => (
                          <div key={i} className="text-[12px] text-gray-700">
                            {[a.street_name, a.city, a.state, a.postcode, a.country].filter(Boolean).join(', ')}
                            {a.is_headquarter && <span className="ml-1.5 text-[9px] uppercase tracking-wide bg-teal-100 text-teal-700 rounded px-1 py-0.5">HQ</span>}
                          </div>
                        ))}
                      </div>
                    </Section>
                  )}
                  {funding.length > 0 && (
                    <Section title="Funding">
                      <div className="space-y-1">
                        {funding.map((f: any, i: number) => (
                          <div key={i} className="text-[12px] text-gray-700">
                            {f.funding_type ? String(f.funding_type).replace(/_/g, ' ') : 'round'}
                            {f.funding_total ? ` · $${Number(f.funding_total).toLocaleString()}` : ''}
                            {f.date_featured ? <span className="text-gray-400"> · {String(f.date_featured).slice(0, 10)}</span> : null}
                          </div>
                        ))}
                      </div>
                    </Section>
                  )}

                </>
              )}
            </div>
          </div>
          );
        })()}
      </div>
    </div>
  );
};

// Phones: filters sit above the results, so jump down to the results when
// a search starts. Laptops: nothing happens.
const scrollToResultsOnPhone = (selector: string) => {
  try {
    if (!window.matchMedia('(max-width: 767px)').matches) return;
    setTimeout(() => document.querySelector(selector)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  } catch { /* ignore */ }
};

// Server messages point at Settings → Subscription with a full URL: show it as a link.
function linkifyError(text: string) {
  const parts = String(text).split(/(https?:\/\/\S+?\/settings\?tab=subscription)/);
  return parts.map((p, i) => (i % 2
    ? <a key={i} href="/settings?tab=subscription" className="underline font-medium">Settings → Subscription</a>
    : <React.Fragment key={i}>{p.replace(/Settings → Subscription: $/, '')}</React.Fragment>));
}

export default V2Lab;
